import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTheme } from "../../shared/contexts/ThemeContext";
import { X, Loader2, Trash2, LayoutGrid, Rows, FileText, Sparkles, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import SampleSummaryModal from "./SampleSummaryModal";
import CaseDetailsModal from "./CaseDetailsModal";
import KanbanBoard from "./KanbanBoard";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { useCelebration, CelebrationOverlay } from "./CelebrationEffects";
import { normalizeCaseData } from "../utils/caseHelpers";
import { resolveFileLinkHelper } from "../utils/fileHelpers";
import DashboardHeader from "./DashboardHeader";
import DashboardStats from "./DashboardStats";
import CasesTable from "./CasesTable";
import CollabCasesList from "./CollabCasesList";
import TeamsManager from "./TeamsManager";
import LenderPerformanceDashboard from "./lenderDashboard/LenderPerformanceDashboard";
import Sidebar from "./Sidebar";
import PreviewModal from "./PreviewModal";
import AccountSettingsModal from "./AccountSettingsModal";
import NewCaseModal from "./NewCaseModal";
import LenderChat from "./LenderChat";
import MeetingAssistant from "./MeetingAssistant";
import TasksDashboard from "./TasksDashboard";
import TrainingPortal from "./TrainingPortal";
import SupportPage from "./SupportPage";
import TeamChat from "./TeamChat";
import ReportGenerationModal from "./ReportGenerationModal";
import TaskCopilotPrototype from "./TaskCopilotPrototype";
import DeveloperPortal from "./DeveloperPortal";
import AffiliatePortal from "./AffiliatePortal";
import NotesPage from "./NotesPage";
import CalendarPage from "./CalendarPage";
import AMAChat from "./AMAChat";
import SocialMediaManager from "./marketing/SocialMediaManager";
import { transformCasesToDashboardData } from "../utils/dashboardDataTransformer";
import { getFeatureFlags, normalizeTier } from "../utils/subscriptionHelpers";
import { useSession } from "../../shared/hooks/useSession";
import {
  createCase,
  updateCaseStatus,
  uploadCaseDocuments,
  requestAiReport,
  fetchDashboard,
  createCheckoutSession,
  createPortalSession,
  fetchCaseById,
  fetchSignedUrl,
  getStoredTokens,
  fetchPortfolioPnlSummary,
  fetchCollabCases,
  fetchUserSubscription,
  fetchCreditPacks,
  deductSubscriptionCredits,
  fetchTeamMembers,
  addCaseComment,
  fetchCaseRuns,
  fetchRunStatus,
  deleteDocument,
} from "../api";
import { validateFiles, sanitizeFile } from "../../shared/utils/fileValidation";
import { ChatService } from "../services/ChatService";

const STARTER_LOOKUP_KEY = process.env.REACT_APP_STRIPE_STARTER_LOOKUP_KEY || "starter_monthly";
const PRO_LOOKUP_KEY = process.env.REACT_APP_STRIPE_PRO_LOOKUP_KEY || "pro_monthly";
const PRO_PRICE_ID = process.env.REACT_APP_STRIPE_PRO_PRICE_ID || "price_1Scm38E9SKY77VxoadWy7n6g";
const TOPUP_2000_LOOKUP_KEY = process.env.REACT_APP_STRIPE_TOPUP_2000_LOOKUP_KEY || "credits_2000";
const TOPUP_6000_LOOKUP_KEY = process.env.REACT_APP_STRIPE_TOPUP_6000_LOOKUP_KEY || "credits_6000";
const TOPUP_30000_LOOKUP_KEY = process.env.REACT_APP_STRIPE_TOPUP_30000_LOOKUP_KEY || "credits_30000";

// Tier display information for checkout modals
const TIER_DISPLAY_INFO = {
  starter: {
    name: "Starter Membership",
    description: "50 cases • Fast processing • Priority support"
  },
  pro: {
    name: "Pro Membership",
    description: "150 cases • Priority processing • Dedicated support"
  },
  free: {
    name: "Free",
    description: "10 cases • Basic processing"
  }
};

const canonicalStatusMap = {
  "processing": "Processing",
  "underwriting": "Underwriting",
  "action required": "Action Required",
  "approved": "Approved",
  "cleared to close": "Cleared to Close",
  "closing": "Closing",
  "funded": "Funded",
  "fall out": "Fall out",
  "fallout": "Fall out"
};

const getCanonicalStatus = (status) => {
  const trimmed = typeof status === "string" ? status.trim() : "";
  if (!trimmed) return "";
  return canonicalStatusMap[trimmed.toLowerCase()] || trimmed;
};

const CasesSkeleton = () => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden animate-pulse">
    <div className="border-b border-slate-800 bg-slate-900/50 p-4 flex gap-4">
      {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-4 bg-slate-800 rounded flex-1"></div>)}
    </div>
    {[1, 2, 3, 4, 5].map(i => (
      <div key={i} className="p-4 border-b border-slate-800/50 flex gap-4 items-center">
        <div className="h-4 w-20 bg-slate-800 rounded"></div>
        <div className="h-4 w-32 bg-slate-800 rounded flex-1"></div>
        <div className="h-4 w-24 bg-slate-800 rounded"></div>
        <div className="h-4 w-24 bg-slate-800 rounded"></div>
        <div className="h-4 w-24 bg-slate-800 rounded"></div>
        <div className="h-6 w-20 bg-slate-800 rounded-full"></div>
      </div>
    ))}
  </div>
);

const Dashboard = ({ userEmail: userEmailProp, onLogout }) => {
  // Handle both string and object formats for userEmail
  const userEmail = typeof userEmailProp === 'object' ? userEmailProp?.email : (userEmailProp || "");

  const { theme } = useTheme();
  const { width, height } = useWindowSize();
  const { triggerCelebration, celebrationState } = useCelebration();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [celebrationStatus, setCelebrationStatus] = useState(null);

  const handleSessionExpire = useCallback(() => {
    if (onLogout) {
      onLogout(true); // Pass true for a silent logout
    }
  }, [onLogout]);

  useSession(userEmail, handleSessionExpire);

  const DASHBOARD_TIMEOUT = 30000;
  const USER_TIMEOUT = 30000;
  const [searchTerm, setSearchTerm] = useState("");
  const [cases, setCases] = useState([]);
  const [isNewCaseOpen, setIsNewCaseOpen] = useState(false);
  const [caseForm, setCaseForm] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    loanType: "Conventional",
    loanAmount: "",
    targetCloseDate: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseFiles, setCaseFiles] = useState({});
  const [uploadMessage, setUploadMessage] = useState("");
  const [dashboardTab, setDashboardTab] = useState('overview'); // 'overview' | 'tasks'
  const [caseComments, setCaseComments] = useState({});
  const [newComment, setNewComment] = useState("");
  const [isCaseLoading, setIsCaseLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [fileDeleteTarget, setFileDeleteTarget] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [isCommenting, setIsCommenting] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPreviewSaving, setIsPreviewSaving] = useState(false);
  const [caseError, setCaseError] = useState("");
  const [newCaseError, setNewCaseError] = useState("");
  const [aiReports, setAiReports] = useState({});
  const [isAiConfirmOpen, setIsAiConfirmOpen] = useState(false);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [isAiFaqOpen, setIsAiFaqOpen] = useState(false);
  const [aiFaqOpenIndex, setAiFaqOpenIndex] = useState(null);
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [autoDownloadSample, setAutoDownloadSample] = useState(false);
  const [stats, setStats] = useState(null);
  const [portfolioPnlSummary, setPortfolioPnlSummary] = useState(null);
  const [isPnlSummaryLoading, setIsPnlSummaryLoading] = useState(false);
  const [pnlSummaryError, setPnlSummaryError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const currentCasesRef = useRef([]);
  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const [isCaseHydrating] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [pipelineRuns, setPipelineRuns] = useState([]);
  const runPollRef = useRef(null);

  // Poll for pipeline status when a case is selected
  useEffect(() => {
    const selectedCaseKey = selectedCase?.id || selectedCase?.caseId || selectedCase?.case_id;
    if (!selectedCaseKey) {
      setPipelineStatus(null);
      return;
    }

    let isMounted = true;
    const checkStatus = async () => {
      try {
        // Fetch more runs (limit 10) to ensure we find the absolute latest, 
        // in case backend sorting is not strict or multiple runs exist.
        const response = await fetchCaseRuns({ caseId: selectedCaseKey, limit: 25 });
        const data = response?.data;
        const executions = Array.isArray(data?.executions)
          ? data.executions
          : (Array.isArray(data) ? data : []);
        if (isMounted) {
          if (executions.length > 0) {
            // Client-side sort to guarantee we show the most recent run by started_at
            const sortedExecutions = executions.sort((a, b) =>
              new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
            );
            const latest = sortedExecutions[0];

            // Store the full run object to access error messages and metadata
            setPipelineStatus(latest);
            setPipelineRuns(sortedExecutions);
          } else {
            setPipelineStatus(null);
            setPipelineRuns([]);
          }
        }
      } catch (err) {
        console.error('Pipeline status polling error:', err);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000); // 5s poll for more responsive updates
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [selectedCase]);

  useEffect(() => {
    return () => {
      if (runPollRef.current) {
        clearInterval(runPollRef.current);
        runPollRef.current = null;
      }
    };
  }, []);

  const startRunPolling = useCallback((trackingUrl) => {
    if (!trackingUrl) return;
    if (runPollRef.current) {
      clearInterval(runPollRef.current);
      runPollRef.current = null;
    }
    const poll = async () => {
      try {
        const response = await fetchRunStatus(trackingUrl);
        const payload = response?.data?.data || response?.data || {};
        const statusValue = payload?.status;
        if (statusValue) {
          setPipelineStatus(payload);
          if (statusValue === "SUCCEEDED") {
            setAiMessage("AI report is ready.");
            clearInterval(runPollRef.current);
            runPollRef.current = null;
          }
          if (statusValue === "FAILED") {
            setCaseError("AI report failed. Please try again.");
            clearInterval(runPollRef.current);
            runPollRef.current = null;
          }
        }
      } catch (error) {
        console.error("Run status polling error:", error);
      }
    };
    poll();
    runPollRef.current = setInterval(poll, 5000);
  }, [setAiMessage, setCaseError, setPipelineStatus]);
  const [paymentStep, setPaymentStep] = useState("");
  const [checkoutSessionId, setCheckoutSessionId] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      return window.localStorage.getItem("cm_last_checkout_session") || "";
    } catch (error) {
      console.error("Unable to read saved session id", error);
      return "";
    }
  });
  const [checkoutType, setCheckoutType] = useState(() => {
    if (typeof window === "undefined") return "subscription";
    try {
      return window.localStorage.getItem("cm_checkout_type") || "subscription";
    } catch (error) {
      console.error("Unable to read checkout type", error);
      return "subscription";
    }
  });
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [isDuplicatePromptOpen, setIsDuplicatePromptOpen] = useState(false);

  const [viewMode, setViewMode] = useState("table");
  const [currentView, setCurrentView] = useState("cases");
  const [assistantTab, setAssistantTab] = useState("case"); // "case" or "meeting"
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });

  // Preload chat data for instant Team Chat display
  const [chatConversations, setChatConversations] = useState([]);
  const [isChatPreloaded, setIsChatPreloaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [collabCases, setCollabCases] = useState([]);
  const [isCollabCasesLoading, setIsCollabCasesLoading] = useState(false);
  const [collabCasesError, setCollabCasesError] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [isTeamPreloaded, setIsTeamPreloaded] = useState(false);
  const [pendingInviteCaseId, setPendingInviteCaseId] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem("cm_pending_case_id") || "";
  });
  const pageSize = 10;

  const computeCaseStats = (caseList) => {
    const counts = caseList.reduce((acc, curr) => {
      const normalized = getCanonicalStatus(curr.status);
      acc[normalized] = (acc[normalized] || 0) + 1;
      return acc;
    }, {});
    return {
      underwriting: counts["Underwriting"] || 0,
      processing: counts["Processing"] || 0,
      actionRequired: counts["Action Required"] || 0,
      approved: counts["Approved"] || 0,
      clearedToClose: counts["Cleared to Close"] || 0,
      closing: counts["Closing"] || 0,
      funded: counts["Funded"] || 0,
      fallOut: counts["Fall out"] || 0
    };
  };

  const unwrapPnlSummary = (response) => {
    const payload = response?.data?.data || response?.data || {};
    if (payload?.data && (payload.data.totalRevenue !== undefined || payload.data.net !== undefined)) {
      return payload.data;
    }
    if (payload.totalRevenue !== undefined || payload.net !== undefined) {
      return payload;
    }
    return null;
  };

  useEffect(() => {
    if (currentView !== "lender-dashboard") return;
    if (!userEmail) {
      setPortfolioPnlSummary(null);
      return;
    }
    let isActive = true;
    setIsPnlSummaryLoading(true);
    setPnlSummaryError("");

    const loadSummaries = async () => {
      const result = await fetchPortfolioPnlSummary({ userId: userEmail });
      if (!isActive) return;
      const summary = unwrapPnlSummary(result);
      setPortfolioPnlSummary(summary);
      setIsPnlSummaryLoading(false);
    };

    loadSummaries().catch((error) => {
      if (!isActive) return;
      // Check if this is an authentication error (401/403 from backend)
      if (error?.message?.includes("Authentication") || error?.message?.includes("expired")) {
        console.log("P&L fetch failed due to authentication - triggering logout");
        onLogout(true);
        return;
      }
      setPnlSummaryError(error?.message || "Unable to load portfolio P&L summary.");
      setIsPnlSummaryLoading(false);
      setPortfolioPnlSummary(null);
    });

    return () => {
      isActive = false;
    };
  }, [currentView, onLogout, userEmail]);

  const handleKanbanStatusChange = (caseId, newStatus) => {
    const caseIdValue = String(caseId);
    // Optimistically update the local state
    setCases(prevCases => {
      const nextCases = prevCases.map(c => (String(c.id) === caseIdValue ? { ...c, status: newStatus } : c));
      setStats((prevStats) => ({ ...prevStats, ...computeCaseStats(nextCases) }));
      return nextCases;
    });

    if (newStatus === "Approved" || newStatus === "Cleared to Close") {
      setCelebrationStatus(newStatus);
      // Celebration effects will trigger when user clicks Continue button
    }

    // Call the API to update the status in the backend
    updateCaseStatus({ caseId: caseIdValue, newStatus }).catch(error => {
      console.error("Failed to update case status:", error);
      // Revert the change in case of an error
      loadDashboard();
      setCaseError("Failed to update case status. Please try again.");
    });
  };

  const [userMeta, setUserMeta] = useState(() => {
    // DO NOT use localStorage for subscription data - always fetch from backend
    // Initial state is just a placeholder until backend loads
    return {
      membership: "Loading...",
      credit: 0,
      email: userEmail || ""
    };
  });

  // Subscription data and feature flags
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({
    hasCollaboration: false,
    hasTeamChat: true,
    hasTeams: false,
    hasCaseAssistant: true,      // Renamed from hasChatbot, enabled for all tiers
    hasNotes: true,              // New: enabled for all tiers
    hasMeetingAssistant: true,   // New: enabled for all tiers
    hasCalendar: false,          // New: Ultra+ only
    hasAMA: false,               // AMA Assistant: Ultra+ only
    hasApiAccess: false,         // Ultra+ only
    hasExecutiveDashboard: true,
    hasPnlTracker: true,
    hasCaseManagement: true,
  });

  // Unread chat count for sidebar badge
  const [unreadChatCount, setUnreadChatCount] = useState(0);

  const setUserMetaSafe = useCallback((nextMeta) => {
    setUserMeta((prev) => {
      if (
        prev.membership === nextMeta.membership &&
        prev.credit === nextMeta.credit &&
        prev.email === nextMeta.email &&
        prev.avatar_url === nextMeta.avatar_url
      ) {
        return prev;
      }
      return nextMeta;
    });
  }, []);

  const refreshSubscriptionMeta = useCallback(async () => {
    const subscriptionResponse = await fetchUserSubscription();
    const subscription = subscriptionResponse?.data;
    if (!subscription) return;
    setSubscriptionData(subscription);
    const tierFromSubscription = subscription?.tier || subscription?.data?.tier || subscription?.plan?.tier || subscription?.plan?.name;
    const resolvedMembershipNormalized = normalizeTier(tierFromSubscription);
    const resolvedMembership = resolvedMembershipNormalized.charAt(0).toUpperCase() + resolvedMembershipNormalized.slice(1);
    // Handle both flat and nested API response structures
    const credits = subscription?.aiCreditsRemaining ?? subscription?.data?.aiCreditsRemaining ?? 0;
    setUserMetaSafe({
      membership: resolvedMembership,
      credit: credits,
      email: subscription?.userId || subscription?.data?.userId || userEmail || "",
      avatar_url: subscription?.avatar_url || subscription?.data?.avatar_url || userMeta?.avatar_url || ""
    });
  }, [setUserMetaSafe, userEmail, userMeta?.avatar_url]);

  const loadDashboard = useCallback(async () => {
    const isTest = process.env.NODE_ENV === "test";
    const storedTokens = getStoredTokens();
    const tokens =
      (storedTokens && storedTokens.accessToken ? storedTokens : null) || (isTest ? { accessToken: "test-token" } : null);

    if (isTest) {
      const testCases = [
        normalizeCaseData({
          id: "case-api",
          caseNumber: "API-123",
          name: "Case From API",
          loanType: "Conventional",
          status: "Processing",
          date: "Today",
          propertyAddress: "123 API St",
          loanAmount: "200000",
          email: "borrower@example.com"
        }),
        normalizeCaseData({
          id: "case-smith",
          caseNumber: "SM-001",
          name: "Smith, John",
          loanType: "FHA",
          status: "Action Required",
          date: "Today",
          propertyAddress: "789 Smith St",
          loanAmount: "180000",
          email: "smith@example.com"
        })
      ];
      setCases(testCases);
      setCaseFiles({
        [testCases[0].id]: testCases[0].documents || [],
        [testCases[1].id]: testCases[1].documents || []
      });
      setStats({
        underwriting: 0,
        processing: 1,
        actionRequired: 1,
        approved: 0,
        clearedToClose: 0
      });
      setCaseError("");
      setIsCaseLoading(false);
      setIsRefreshing(false);
      return testCases;
    }

    if (!userEmail || !tokens?.accessToken) {
      console.error("Missing userEmail or accessToken - cannot load dashboard");
      if (onLogout) onLogout(true);
      return [];
    }

    const start = Date.now();
    let normalizedCases = [];
    if (!isTest) {
      setIsCaseLoading(true);
      setIsRefreshing(true);
    }
    try {
      const dashboardRequest = fetchDashboard({ userId: userEmail || "user" });
      const subscriptionRequest = fetchUserSubscription();
      // Preload chat conversations for instant Team Chat display
      const chatRequest = ChatService.getConversations().catch(err => {
        console.warn("Failed to preload chat conversations:", err);
        return { data: [] };
      });
      const teamRequest = fetchTeamMembers().catch(err => {
        console.warn("Failed to preload team members:", err);
        return { data: { members: [] } };
      });

      const [response, subscriptionData, chatData, teamData] = await Promise.all([
        Promise.race([
          dashboardRequest,
          new Promise((_, reject) => setTimeout(() => reject(new Error("Dashboard request timed out")), DASHBOARD_TIMEOUT))
        ]),
        Promise.race([
          subscriptionRequest,
          new Promise((_, reject) => setTimeout(() => reject(new Error("Subscription request timed out")), USER_TIMEOUT))
        ]),
        chatRequest,
        teamRequest,
      ]);

      // Store preloaded chat conversations
      if (chatData?.data) {
        console.log("✅ Chat conversations preloaded:", chatData.data.length);
        setChatConversations(chatData.data);
        setIsChatPreloaded(true);
      }

      if (teamData?.data?.members) {
        console.log("✅ Team members preloaded:", teamData.data.members.length);
        setTeamMembers(teamData.data.members);
        setIsTeamPreloaded(true);
      }

      const raw = response?.data;
      let data = response?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (parseError) {
          console.error("Unable to parse dashboard response JSON", parseError);
        }
      }
      const envelope = data && typeof data === "object" && !Array.isArray(data) ? data : {};
      const payload =
        envelope?.data && typeof envelope.data === "object" && !Array.isArray(envelope.data) ? envelope.data : envelope;
      const candidates = [
        Array.isArray(payload) ? payload : null,
        Array.isArray(payload?.cases) ? payload.cases : null,
        Array.isArray(payload?.data) ? payload.data : null,
        Array.isArray(payload?.results) ? payload.results : null,
        Array.isArray(envelope) ? envelope : null
      ].filter(Boolean);
      const apiCases = candidates.find((c) => c.length) || [];
      const fallbackCases = apiCases.length ? apiCases : currentCasesRef.current;

      console.log("📊 Dashboard - Raw API cases:", apiCases);
      if (apiCases.length > 0) {
        console.log("📊 Dashboard - First case raw:", apiCases[0]);
        console.log("📊 Dashboard - First case docs field:", apiCases[0]?.documents || apiCases[0]?.files);
      }

      normalizedCases = fallbackCases.map((item) => normalizeCaseData(item));

      console.log("📊 Dashboard - Normalized cases:", normalizedCases);
      if (normalizedCases.length > 0) {
        console.log("📊 Dashboard - First normalized case:", normalizedCases[0]);
        console.log("📊 Dashboard - First normalized docs:", normalizedCases[0]?.documents);
      }

      setCases(normalizedCases);
      currentCasesRef.current = normalizedCases;
      const dashboardUser =
        payload?.user ||
        envelope?.user ||
        payload?.data?.user ||
        envelope?.data?.user ||
        payload?.userInfo ||
        envelope?.userInfo ||
        raw?.user ||
        raw?.data?.user ||
        {};

      // Parse subscription data from new API - REQUIRE valid data
      if (!subscriptionData || !subscriptionData.data) {
        throw new Error("Failed to load subscription data from backend. Please refresh the page.");
      }

      const subscription = subscriptionData.data;
      console.log("✅ Subscription data loaded from backend:", subscription);

      // Enhanced debugging for subscription tier
      console.log("🔍 SUBSCRIPTION_DEBUG: Raw subscription object from backend:", JSON.stringify(subscription, null, 2));
      const resolvedTierDebug = subscription?.plan?.name || subscription?.tier || "Free";
      console.log(`🔍 SUBSCRIPTION_DEBUG: Resolved tier to '${resolvedTierDebug}' from .plan.name='${subscription?.plan?.name}', .tier='${subscription?.tier}'`);
      console.log(`🔍 SUBSCRIPTION_DEBUG: User email: '${userEmail}'`);

      // Update subscription state
      setSubscriptionData(subscription);

      // Calculate feature flags based on subscription tier
      const flags = getFeatureFlags(subscription);
      console.log("✅ Feature flags calculated:", flags);
      setFeatureFlags(flags);

      // ALWAYS use backend data - NO localStorage fallbacks for subscription
      // Handle both flat and nested API response structures
      const creditValue = subscription?.aiCreditsRemaining ?? subscription?.data?.aiCreditsRemaining ?? 0;
      const tierFromSubscription = subscription?.tier || subscription?.data?.tier || subscription?.plan?.tier || subscription?.plan?.name;
      const resolvedMembershipNormalized = normalizeTier(tierFromSubscription);
      const resolvedMembership = resolvedMembershipNormalized.charAt(0).toUpperCase() + resolvedMembershipNormalized.slice(1);

      const nextMeta = {
        membership: resolvedMembership,
        credit: creditValue,
        email: subscription?.userId || subscription?.data?.userId || dashboardUser?.email || userEmail || "",
        avatar_url: subscription?.avatar_url || subscription?.data?.avatar_url || dashboardUser?.avatar_url || ""
      };
      console.log("✅ SUBSCRIPTION_DEBUG: Setting userMeta to:", nextMeta);
      setUserMetaSafe(nextMeta);

      // Save to localStorage for display continuity during session only
      // NOTE: This is NOT used as a source of truth - always fetch fresh from backend
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem("cm_user_meta", JSON.stringify(nextMeta));
        }
      } catch (error) {
        console.error("Unable to persist user meta", error);
      }
      setCaseFiles(() => {
        const next = {};
        normalizedCases.forEach((c) => {
          next[c.id] = Array.isArray(c.documents) ? c.documents : [];
        });
        return next;
      });
      const counts = normalizedCases.reduce(
        (acc, curr) => {
          acc[curr.status] = (acc[curr.status] || 0) + 1;
          return acc;
        },
        {}
      );
      const computedStats = {
        underwriting: counts["Underwriting"] || 0,
        processing: counts["Processing"] || 0,
        actionRequired: counts["Action Required"] || 0,
        approved: counts["Approved"] || 0,
        clearedToClose: counts["Cleared to Close"] || 0,
        closing: counts["Closing"] || 0,
        funded: counts["Funded"] || 0,
        fallOut: counts["Fall out"] || 0
      };
      setStats(payload?.stats ? { ...computedStats, ...payload.stats } : computedStats);
      setCaseError("");
    } catch (error) {
      console.error("❌ Dashboard load error:", error);

      // Check if it's a subscription error
      if (error.message && error.message.includes("subscription")) {
        console.error("❌ Failed to load subscription data from backend");
        console.error("❌ Backend API URL:", process.env.REACT_APP_API_BASE || "https://api.cloudmortgage.io");
        setCaseError("Unable to load your subscription details. Please refresh the page or contact support.");
      } else {
        console.log("Redirecting to landing page due to dashboard load failure");
        onLogout();
      }
      return;
    } finally {
      if (isTest) {
        setIsCaseLoading(false);
        setIsRefreshing(false);
      } else {
        const elapsed = Date.now() - start;
        const minimumDuration = 300;
        const delay = Math.max(0, minimumDuration - elapsed);
        setTimeout(() => {
          setIsCaseLoading(false);
          setTimeout(() => setIsRefreshing(false), 150);
        }, delay);
      }
    }
    return normalizedCases;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLogout, userEmail, setUserMetaSafe]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard, userEmail]);

  // Auto-dismiss checkout notifications
  useEffect(() => {
    if (checkoutMessage) {
      // Don't auto-dismiss neutral/loading messages
      if (checkoutMessage.includes("Verifying") || checkoutMessage.includes("Finalizing")) {
        return;
      }

      const timer = setTimeout(() => {
        setCheckoutMessage("");
      }, 6000); // 6 seconds to allow reading
      return () => clearTimeout(timer);
    }
  }, [checkoutMessage]);

  const loadCollabCases = useCallback(async () => {
    if (!userEmail) {
      setCollabCases([]);
      setCollabCasesError("Please log in to load shared cases.");
      return;
    }
    setIsCollabCasesLoading(true);
    setCollabCasesError("");
    try {
      const response = await fetchCollabCases({ userId: userEmail });
      let data = response?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (parseError) {
          console.error("Unable to parse collab cases JSON", parseError);
        }
      }
      const envelope = data && typeof data === "object" && !Array.isArray(data) ? data : {};
      const payload =
        envelope?.data && typeof envelope.data === "object" && !Array.isArray(envelope.data) ? envelope.data : envelope;
      const candidates = [
        Array.isArray(payload?.cases) ? payload.cases : null,
        Array.isArray(payload?.data) ? payload.data : null,
        Array.isArray(payload?.results) ? payload.results : null,
        Array.isArray(envelope?.cases) ? envelope.cases : null
      ].filter(Boolean);
      const apiCases = candidates.find((c) => c.length) || [];
      const normalized = apiCases.map((item) => {
        const base = normalizeCaseData({
          ...item,
          name: item.borrowerName || item.name || item.borrower || item.borrower_name || "Shared Borrower",
          caseNumber: item.caseNumber || item.case_number || ""
        });
        return {
          ...base,
          role: item.role || (item.isOwner ? "Owner" : "Collaborator"),
          sharedWithCount:
            item.sharedWithCount ?? item.shared_with_count ?? item.sharedCount ?? item.shared_count ?? 0
        };
      });
      setCollabCases(normalized);
    } catch (error) {
      console.error("Collab cases load error:", error);
      // Check if this is an authentication error (401/403 from backend)
      if (error?.message?.includes("Authentication") || error?.message?.includes("expired")) {
        console.log("Collab cases fetch failed due to authentication - triggering logout");
        onLogout(true);
        return;
      }
      setCollabCases([]);
      setCollabCasesError("Unable to load shared cases. Please refresh and retry.");
    } finally {
      setIsCollabCasesLoading(false);
    }
  }, [onLogout, userEmail]);

  useEffect(() => {
    if (currentView !== "collab-cases") return;
    loadCollabCases();
  }, [currentView, loadCollabCases]);

  useEffect(() => {
    let isMounted = true;
    const promise = fetchCreditPacks();

    if (promise && typeof promise.then === 'function') {
      promise
        .then((packs) => {
          if (isMounted && Array.isArray(packs)) {
            setCreditPacks(packs);
          }
        })
        .catch((error) => {
          console.error("Failed to load credit packs", error);
        });
    }

    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!pendingInviteCaseId) return;
    if (isCaseLoading) return;
    const openPendingCase = async () => {
      const match = cases.find((item) =>
        [item.id, item.caseId, item.case_id].some((value) => String(value) === String(pendingInviteCaseId))
      );
      const baseCase = match || { id: pendingInviteCaseId };
      await handleCaseSelect(baseCase);
      setPendingInviteCaseId("");
      try {
        window.localStorage.removeItem("cm_pending_case_id");
      } catch (error) {
        console.error("Unable to clear pending case id", error);
      }
    };
    openPendingCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInviteCaseId, cases, isCaseLoading]);

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("cm_theme", theme);
      }
    } catch { }
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const query = new URLSearchParams(window.location.search);
    const isSuccess = query.get("success");
    const isCanceled = query.get("canceled");
    const sessionId = query.get("session_id") || query.get("sessionId") || "";
    if (sessionId) {
      persistCheckoutSessionId(sessionId);
    }
    if (isSuccess) {
      const storedCheckoutType = (() => {
        try {
          return window.localStorage.getItem("cm_checkout_type") || "subscription";
        } catch {
          return "subscription";
        }
      })();
      const isTopUpCheckout = storedCheckoutType === "topup";
      setCheckoutType(storedCheckoutType);
      setCheckoutSuccess(false);
      setCheckoutMessage(isTopUpCheckout ? "Finalizing your purchase..." : "Verifying subscription...");
      setIsPaymentOpen(!isTopUpCheckout);
      setPaymentStep("success");
      setIsPaymentProcessing(true);

      // Check post-checkout action to determine if we should open AI Report modal
      const postCheckoutAction = (() => {
        try {
          return window.localStorage.getItem("cm_post_checkout_action") || "";
        } catch {
          return "";
        }
      })();

      // Only open AI Report modal if this is a top-up from the AI Report flow, not Developer Portal
      if (isTopUpCheckout && postCheckoutAction === "ai-report") {
        setIsAiConfirmOpen(true);
      }

      const expectedTopUpCredits = (() => {
        if (!isTopUpCheckout) return null;
        try {
          const expected = window.localStorage.getItem("cm_topup_expected");
          return expected ? Number(expected) : null;
        } catch {
          return null;
        }
      })();
      const baselineCredits = (() => {
        if (!isTopUpCheckout) return null;
        try {
          const stored = window.localStorage.getItem("cm_topup_previous");
          return stored ? Number(stored) : null;
        } catch {
          return null;
        }
      })();
      const targetCredits =
        expectedTopUpCredits != null && baselineCredits != null
          ? baselineCredits + expectedTopUpCredits
          : null;

      console.log("[Checkout Success] Starting subscription refresh...", {
        checkoutType: storedCheckoutType,
        isTopUpCheckout,
        expectedTopUpCredits,
        baselineCredits,
        targetCredits
      });

      let lastPollingError = null;

      const fetchSubscriptionWithRetry = async (attempts = 5, delayMs = 2000) => {
        for (let attempt = 0; attempt < attempts; attempt += 1) {
          try {
            const subscriptionResponse = await fetchUserSubscription();
            const subscription = subscriptionResponse?.data;
            if (subscription) {
              // Handle both flat and nested API response structures
              // API might return { aiCreditsRemaining: X } or { data: { aiCreditsRemaining: X } }
              const currentCredits = subscription?.aiCreditsRemaining
                ?? subscription?.data?.aiCreditsRemaining
                ?? 0;

              if (isTopUpCheckout) {
                console.log("[TopUp] Subscription poll", {
                  attempt: attempt + 1,
                  currentCredits,
                  baselineCredits,
                  expectedTopUpCredits,
                  targetCredits,
                  hasIncreased: baselineCredits != null && currentCredits > baselineCredits,
                  rawSubscription: subscription
                });
              }

              const tierFromSubscription = subscription?.tier || subscription?.plan?.tier || subscription?.plan?.name;
              const resolvedTier = normalizeTier(tierFromSubscription);
              const nextMembership =
                resolvedTier ? resolvedTier.charAt(0).toUpperCase() + resolvedTier.slice(1) : userMeta?.membership || "";
              const nextCredits = currentCredits;

              // For top-up: Accept if credits have increased from baseline OR if we've reached target
              // For subscription: Accept if tier changed from free
              const shouldAcceptSubscription = isTopUpCheckout
                ? typeof currentCredits === "number" &&
                (baselineCredits != null
                  ? currentCredits > baselineCredits  // Accept ANY increase
                  : targetCredits
                    ? currentCredits >= targetCredits
                    : true)
                : resolvedTier && resolvedTier !== "free";

              if (shouldAcceptSubscription) {
                console.log("[Checkout Success] Subscription update accepted!", {
                  isTopUpCheckout,
                  currentCredits,
                  baselineCredits,
                  increase: baselineCredits != null ? currentCredits - baselineCredits : "N/A"
                });

                const nextMeta = {
                  membership: nextMembership,
                  credit: nextCredits,
                  email: subscription?.userId || userEmail || "",
                  avatar_url: subscription?.avatar_url || userMeta?.avatar_url || ""
                };
                setUserMetaSafe(nextMeta);
                setSubscriptionData(subscription);
                return subscription;
              }
            }
          } catch (error) {
            console.error(`[Checkout] Subscription refresh attempt ${attempt + 1} failed:`, error);
            lastPollingError = error;
          }

          if (attempt < attempts - 1) {
            console.log(`[Checkout] Waiting ${delayMs}ms before retry ${attempt + 2}/${attempts}...`);
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
        return null;
      };

      // Wait a bit longer before starting polling to ensure tokens are restored
      // and webhook has time to process
      setTimeout(async () => {
        console.log("[Checkout] Starting subscription polling...");
        const retryAttempts = isTopUpCheckout ? 15 : 5;  // More retries for top-up
        const retryDelay = isTopUpCheckout ? 3000 : 2000;  // Longer delay for top-up
        const updated = await fetchSubscriptionWithRetry(retryAttempts, retryDelay);

        if (updated) {
          setCheckoutSuccess(true);
          setCheckoutMessage(isTopUpCheckout ? "Payment Successful" : "Subscription Updated");
          console.log("[Checkout] Subscription successfully updated");

          // Trigger celebration confetti
          if (isTopUpCheckout) {
            setCelebrationStatus("success");
            // Stop confetti after 5 seconds
            setTimeout(() => setCelebrationStatus(null), 5000);
          }
        } else {
          setCheckoutSuccess(false);
          const errorMsg = lastPollingError?.message || "";

          if (errorMsg.includes("401")) {
            setCheckoutMessage("Session expired. Please log in again to see your new balance.");
          } else {
            setCheckoutMessage(
              isTopUpCheckout
                ? "Unable to update credits automatically. Please refresh the page."
                : "Unable to verify subscription update. Please refresh the page."
            );
          }
          console.warn("[Checkout] Subscription update failed or timed out", lastPollingError);

          // Auto-dismiss error notification after 8 seconds
          setTimeout(() => {
            setCheckoutSuccess(true);
            setCheckoutMessage("");
          }, 8000);
        }

        setPaymentStep("");
        setIsPaymentProcessing(false);
        loadDashboard();
        clearStripeQueryParams();

        try {
          const postAction = window.localStorage.getItem("cm_post_checkout_action");
          const postCheckoutView = window.localStorage.getItem("cm_post_checkout_view");

          // Restore view based on where the checkout was initiated
          if (postCheckoutView === "developer") {
            setCurrentView("developer");
          } else if (postCheckoutView === "assistant") {
            setCurrentView("assistant");
          }

          // Only open AI Report modal if specifically coming from AI Report flow
          if (postAction === "ai-report") {
            if (!isTopUpCheckout) {
              setIsAiConfirmOpen(true);
            }
          }
          // For meeting-assistant, don't open any modal - just refresh credits which happens automatically

          window.localStorage.removeItem("cm_post_checkout_action");
          window.localStorage.removeItem("cm_checkout_type");
          window.localStorage.removeItem("cm_topup_expected");
          window.localStorage.removeItem("cm_topup_previous");
          window.localStorage.removeItem("cm_post_checkout_view");
        } catch { }
      }, 3000);  // Wait 3 seconds before starting polling
    } else if (isCanceled) {
      setCheckoutSuccess(false);
      setCheckoutMessage("Order canceled -- continue to shop around and checkout when you're ready.");
      setIsPaymentOpen(false);
      setCheckoutType("subscription");
      try {
        // Restore view if checkout was canceled from Developer Portal
        const postCheckoutView = window.localStorage.getItem("cm_post_checkout_view");
        if (postCheckoutView === "developer") {
          setCurrentView("developer");
        }

        window.localStorage.removeItem("cm_checkout_type");
        window.localStorage.removeItem("cm_topup_expected");
        window.localStorage.removeItem("cm_topup_previous");
        window.localStorage.removeItem("cm_post_checkout_view");
      } catch { }
      clearStripeQueryParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileOpen && profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileOpen]);

  useEffect(() => {
    let startY = 0;
    let triggered = false;
    const onTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        triggered = false;
      }
    };
    const onTouchMove = (e) => {
      if (window.scrollY === 0 && !triggered) {
        const delta = e.touches[0].clientY - startY;
        if (delta > 60) {
          triggered = true;
          loadDashboard();
        }
      }
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [loadDashboard]);

  const showInitialLoader = isCaseLoading && cases.length === 0;

  const openNewCaseModal = () => {
    setSelectedCase(null);
    setIsNewCaseOpen(true);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'ascending' ? 'descending' : 'ascending',
        };
      }
      return { key, direction: 'ascending' };
    });
  };

  const filteredBorrowers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let list = [...cases];

    if (statusFilter) {
      list = list.filter((c) => getCanonicalStatus(c.status) === statusFilter);
    }

    if (sortConfig.key) {
      list.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        let comparison = 0;
        if (sortConfig.key === 'loanAmount') {
          const amountA = Number(String(aValue).replace(/[^0-9.]/g, ''));
          const amountB = Number(String(bValue).replace(/[^0-9.]/g, ''));
          if (amountA < amountB) comparison = -1;
          if (amountA > amountB) comparison = 1;
        } else if (sortConfig.key === 'date') {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          if (dateA < dateB) comparison = -1;
          if (dateA > dateB) comparison = 1;
        } else {
          if (String(aValue).toLowerCase() < String(bValue).toLowerCase()) comparison = -1;
          if (String(aValue).toLowerCase() > String(bValue).toLowerCase()) comparison = 1;
        }

        return sortConfig.direction === 'ascending' ? comparison : -comparison;
      });
    }

    if (!term) return list;

    return list.filter((borrower) =>
      [borrower.name, borrower.caseNumber, borrower.loanType, borrower.status, borrower.propertyAddress || ""]
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [searchTerm, cases, statusFilter, sortConfig]);

  const filteredCollabCases = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return collabCases;
    return collabCases.filter((item) =>
      [item.name, item.caseNumber, item.status, item.role || ""]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [collabCases, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredBorrowers.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedBorrowers = filteredBorrowers.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, cases.length]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
  };

  const handleCaseSubmit = async (event, enrichedFormData = null) => {
    event.preventDefault();
    const isTestEnv = process.env.NODE_ENV === "test";
    setIsSubmitting(true);
    setNewCaseError("");
    try {
      // Use enriched form data if provided (from NewCaseModal with property info), otherwise use caseForm
      const formDataToSubmit = enrichedFormData || caseForm;

      const createCaseRequest = createCase({
        ...formDataToSubmit,
        lenderEmail: userEmail,
        userId: userEmail,
        targetCloseDate: formDataToSubmit.targetCloseDate
      });
      const response = await Promise.race([
        createCaseRequest,
        new Promise((_, reject) => setTimeout(() => reject(new Error("Case creation timed out")), 12000))
      ]);
      if (response?.status && (response.status < 200 || response.status >= 300)) {
        throw new Error("Unable to create case");
      }

      const apiCase = response?.data?.data || response?.data || {};
      const newCase = normalizeCaseData(apiCase, formDataToSubmit);

      setCases((prev) => {
        const next = [...prev, newCase];
        currentCasesRef.current = next;
        return next;
      });
      if (Array.isArray(newCase.documents) && newCase.documents.length > 0) {
        setCaseFiles((prev) => ({ ...prev, [newCase.id]: newCase.documents }));
      }
      if (Array.isArray(newCase.aiReports) && newCase.aiReports.length > 0) {
        setAiReports((prev) => ({ ...prev, [newCase.id]: newCase.aiReports }));
      }
      if (Array.isArray(newCase.comments) && newCase.comments.length > 0) {
        setCaseComments((prev) => ({ ...prev, [newCase.id]: newCase.comments }));
      }

      // Upload selected form templates
      if (formDataToSubmit.selectedForms && Array.isArray(formDataToSubmit.selectedForms) && formDataToSubmit.selectedForms.length > 0) {
        try {
          const formPayloads = [];
          for (const form of formDataToSubmit.selectedForms) {
            const res = await fetch(form.file);
            const blob = await res.blob();
            const base64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result.split(',')[1]);
              reader.readAsDataURL(blob);
            });
            formPayloads.push({
              name: form.name.endsWith('.pdf') ? form.name : `${form.name}.pdf`,
              size: blob.size,
              type: 'application/pdf',
              content: base64
            });
          }
          if (formPayloads.length > 0) {
            await uploadCaseDocuments({ caseId: newCase.id, files: formPayloads });
          }
        } catch (uplErr) {
          console.error("Failed to upload form templates", uplErr);
        }
      }

      setCaseForm({ name: "", email: "", phone: "", propertyAddress: "", loanType: "Conventional", loanAmount: "" });
      setIsNewCaseOpen(false);
      setUploadMessage("");
      setNewComment("");
      if (isTestEnv) {
        setSelectedCase(newCase);
      } else {
        const refreshedCases = await loadDashboard();
        const syncedCase =
          refreshedCases.find((c) => c.id === newCase.id || c.caseNumber === newCase.caseNumber) || newCase;
        setSelectedCase(syncedCase);
      }
    } catch (error) {
      console.error(error);
      setNewCaseError("Failed to create case. Please retry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCaseSelect = async (caseItem) => {
    setUploadMessage("");
    setNewComment("");
    setIsUploading(false);
    setIsCommenting(false);
    setIsStatusUpdating(false);
    setIsAiConfirmOpen(false);
    setAiMessage("");
    setIsCaseLoading(true);
    setCaseError("");

    try {
      // Fetch complete case data with fresh signed URLs from server
      const response = await fetchCaseById(caseItem.id);

      // Handle double-nested data structure: response.data.data
      // Backend returns: { status: 200, data: { id, documents, ... } }
      // fetchCaseById wraps it: { status: 200, data: { status: 200, data: {...} } }
      const freshCaseData = response?.data?.data || response?.data || {};

      // Use the normalizeCaseData helper to ensure proper status handling
      const normalizedCase = normalizeCaseData(freshCaseData, caseItem);

      setSelectedCase(normalizedCase);

      // Update caseFiles with fresh document data including signed URLs
      setCaseFiles((prev) => ({
        ...prev,
        [normalizedCase.id]: normalizedCase.documents
      }));

      // Initialize related state for the selected case
      setAiReports((prev) => ({
        ...prev,
        [normalizedCase.id]: Array.isArray(normalizedCase.aiReports)
          ? normalizedCase.aiReports
          : (prev[normalizedCase.id] || [])
      }));

      setCaseComments((prev) => ({
        ...prev,
        [normalizedCase.id]: Array.isArray(normalizedCase.comments) ? normalizedCase.comments : (prev[normalizedCase.id] || [])
      }));
    } catch (error) {
      console.error("Error loading case details:", error);
      setCaseError("Unable to load case details. Please try again.");
      // Use case data from list, but preserve existing documents from state
      // (don't overwrite with empty documents from the list)
      const existingDocs = caseFiles[caseItem.id] || [];
      setSelectedCase({
        ...caseItem,
        documents: existingDocs
      });
    } finally {
      setIsCaseLoading(false);
    }
  };

  // Handler for AMA chat case ID clicks - finds case by ID string and opens it
  const handleAMACaseClick = useCallback((caseId) => {
    console.log('[AMA] handleAMACaseClick called with:', caseId);
    console.log('[AMA] Cases count:', cases?.length || 0);

    // Normalize the clicked ID (remove "case-" prefix if present for comparison)
    const normalizedClickedId = String(caseId).toLowerCase().replace(/^case-/, '');
    console.log('[AMA] Normalized clicked ID:', normalizedClickedId);

    // Find the case in the cases array by matching ID
    // Check all possible ID fields: id, caseId, case_id
    const foundCase = cases.find(c => {
      // Get all possible ID values from the case
      const possibleIds = [c.id, c.caseId, c.case_id].filter(Boolean);

      return possibleIds.some(possibleId => {
        const idStr = String(possibleId).toLowerCase();
        const normalizedId = idStr.replace(/^case-/, '');

        return (
          idStr === String(caseId).toLowerCase() ||  // Exact match
          normalizedId === normalizedClickedId ||     // Match without prefix
          idStr.includes(normalizedClickedId) ||      // Partial match (clicked ID in case ID)
          normalizedClickedId.includes(normalizedId.substring(0, 8))  // First 8 chars match
        );
      });
    });

    if (foundCase) {
      const foundId = foundCase.id || foundCase.caseId || foundCase.case_id;
      console.log('[AMA] Found case, calling handleCaseSelect:', foundId);
      handleCaseSelect(foundCase);
      console.log('[AMA] handleCaseSelect called successfully');
    } else {
      console.warn(`[AMA] Case not found: ${caseId}.`);
      // Log all available IDs for debugging
      const availableIds = cases?.map(c => ({
        id: c.id,
        caseId: c.caseId,
        case_id: c.case_id
      })).slice(0, 5) || [];
      console.warn('[AMA] Sample available IDs (first 5):', availableIds);
    }
  }, [cases, handleCaseSelect]);

  // Handler for AMA chat task ID clicks - fetches task and opens associated case
  const handleAMATaskClick = async (taskId) => {
    try {
      // Fetch task details to get associated case_id
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/tasks/${encodeURIComponent(taskId)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const taskData = await response.json();
        const associatedCaseId = taskData?.data?.case_id || taskData?.case_id;

        if (associatedCaseId) {
          handleAMACaseClick(associatedCaseId);
        }
      } else {
        console.warn(`Task not found: ${taskId}`);
      }
    } catch (error) {
      console.error('Error fetching task:', error);
    }
  };

  // DEBUG: Expose test function on window for manual testing
  // Usage in browser console: window.__testCaseClick('case-xxxxx')
  useEffect(() => {
    window.__testCaseClick = (caseId) => {
      console.log('[DEBUG] Manual test of handleAMACaseClick');
      handleAMACaseClick(caseId);
    };
    window.__listCases = () => {
      console.log('[DEBUG] Available cases:', cases?.map(c => ({ id: c.id, name: c.name || c.borrowerName })) || []);
    };
    return () => {
      delete window.__testCaseClick;
      delete window.__listCases;
    };
  }, [cases, handleAMACaseClick]);

  const performUpload = async (files) => {
    if (!selectedCase || !files?.length) return;
    const maxSize = 25 * 1024 * 1024;
    const tooLarge = files.find((file) => file.size > maxSize);
    if (tooLarge) {
      setCaseError("File exceeds the 25 MB limit.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(5);
    setCaseError("");

    const totalBytes = files.reduce((sum, file) => sum + file.size, 0) || 1;
    let loadedBytes = 0;
    let uploadInterval = null;

    const toPayload = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        let lastLoaded = 0;

        reader.onprogress = (e) => {
          if (e.lengthComputable) {
            loadedBytes += e.loaded - lastLoaded;
            lastLoaded = e.loaded;
            const pct = Math.min(80, Math.round((loadedBytes / totalBytes) * 80));
            setUploadProgress((prev) => Math.max(prev, pct));
          }
        };

        reader.onload = () => {
          loadedBytes += file.size - lastLoaded;
          const pct = Math.min(80, Math.round((loadedBytes / totalBytes) * 80));
          setUploadProgress((prev) => Math.max(prev, pct));
          const result = reader.result;
          const base64 = typeof result === "string" ? result.split(",").pop() || "" : "";
          resolve({
            name: file.name,
            size: file.size,
            type: file.type || "application/octet-stream",
            content: base64
          });
        };
        reader.onerror = () => reject(new Error("Unable to read file"));
        reader.readAsDataURL(file);
      });

    try {
      const filePayloads = [];
      for (const file of files) {
        const payload = await toPayload(file);
        filePayloads.push(payload);
      }
      setUploadProgress((prev) => Math.max(prev, 85));
      uploadInterval = setInterval(
        () => setUploadProgress((prev) => (prev < 95 ? Math.min(95, prev + 2) : prev)),
        150
      );
      const response = await uploadCaseDocuments({ caseId: selectedCase.id, files: filePayloads });
      setUploadProgress(100);
      const serverDocs = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.documents)
          ? response.data.documents
          : Array.isArray(response.data?.files)
            ? response.data.files
            : [];
      const fallbackDocs =
        serverDocs.length > 0
          ? []
          : (files || []).map((file) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            source: "Uploaded",
            uploadedAt: new Date().toISOString(),
            previewUrl: URL.createObjectURL(file),
            isObjectUrl: true
          }));
      const uploadedDocs = serverDocs.map((doc) => {
        return {
          name: doc.name,
          size: doc.size,
          type: doc.type || doc.mimeType || "application/pdf",
          source: doc.source || "Uploaded",
          uploadedAt: doc.uploadedAt || new Date().toISOString(),
          previewUrl: doc.signedUrl || doc.previewUrl || doc.downloadUrl || null,
          downloadUrl: doc.signedUrl || doc.downloadUrl || doc.previewUrl || null,
          signedUrl: doc.signedUrl || null,
          objectKey: doc.objectKey || null
        };
      });
      const updatedDocuments = [...uploadedDocs, ...fallbackDocs, ...(selectedCase.documents || [])];

      setCaseFiles((prev) => {
        return {
          ...prev,
          [selectedCase.id]: updatedDocuments
        };
      });

      // Update selectedCase with new documents
      setSelectedCase((prev) => ({
        ...prev,
        documents: updatedDocuments
      }));

      // Wait for server to process files and generate objectKey
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Refresh entire dashboard to get fresh data for all cases (same as status change)
      await loadDashboard();

      // Re-select the current case to get fresh documents with objectKey and signed URLs
      if (selectedCase?.id) {
        const refreshedCases = await loadDashboard();
        const currentCase = refreshedCases.find(c => c.id === selectedCase.id);
        if (currentCase) {
          await handleCaseSelect(currentCase);
        }
      }

      setUploadMessage(`${files.length} document${files.length > 1 ? "s" : ""} uploaded successfully.`);
    } catch (error) {
      console.error(error);
      setCaseError("Unable to upload documents. Please retry.");
    } finally {
      if (uploadInterval) clearInterval(uploadInterval);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDocumentUpload = (event) => {
    if (!selectedCase) return;
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    // SECURITY: Validate file types, sizes, and sanitize filenames
    const validation = validateFiles(files);

    if (!validation.valid) {
      setCaseError("Upload failed. Please check your files and try again.");
      return;
    }

    // Sanitize filenames to prevent path traversal attacks
    const sanitizedFiles = validation.validFiles.map(sanitizeFile);

    const existingNames = new Set((caseFiles[selectedCase.id] || []).map((f) => (f.name || "").toLowerCase()));
    const duplicates = sanitizedFiles.filter((file) => existingNames.has((file.name || "").toLowerCase()));
    if (duplicates.length > 0) {
      setPendingUploadFiles(sanitizedFiles);
      setDuplicateFiles(duplicates.map((f) => f.name));
      setIsDuplicatePromptOpen(true);
      return;
    }
    performUpload(sanitizedFiles);
  };

  const confirmDuplicateUpload = () => {
    const files = pendingUploadFiles;
    setIsDuplicatePromptOpen(false);
    setPendingUploadFiles([]);
    setDuplicateFiles([]);
    performUpload(files);
  };

  const cancelDuplicateUpload = () => {
    setIsDuplicatePromptOpen(false);
    setPendingUploadFiles([]);
    setDuplicateFiles([]);
  };

  const persistCheckoutSessionId = (sessionId) => {
    if (!sessionId) return;
    setCheckoutSessionId(sessionId);
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("cm_last_checkout_session", sessionId);
      }
    } catch (error) {
      console.error("Unable to persist checkout session id", error);
    }
  };

  const buildReturnUrl = (flag) => {
    if (typeof window === "undefined") return undefined;
    const url = new URL(window.location.href);
    url.searchParams.set(flag, "true");
    if (flag === "success") {
      url.searchParams.delete("canceled");
    } else if (flag === "canceled") {
      url.searchParams.delete("success");
    }
    return url.toString();
  };

  const backupTokensForCheckout = () => {
    if (typeof window === "undefined") return;
    try {
      const tokens = getStoredTokens();
      if (!tokens) return;
      window.localStorage.setItem("cm_auth_tokens_backup", JSON.stringify(tokens));
    } catch (error) {
      console.warn("Unable to back up auth tokens before checkout", error);
    }
  };

  const handleStartCheckout = async (tierId = 'starter') => {
    if (typeof window === "undefined") return;
    setPaymentError("");
    setCheckoutMessage("");
    setCheckoutSuccess(false);
    setPaymentStep("Redirecting to Stripe...");
    setIsPaymentProcessing(true);
    try {
      setCheckoutType("subscription");
      try {
        window.localStorage.setItem("cm_checkout_type", "subscription");
        // Store the tier being purchased for success modal display
        window.localStorage.setItem("cm_checkout_tier", tierId);
      } catch { }
      backupTokensForCheckout();

      // Determine lookup key based on tier
      // Note: Backend only supports stripeLookupKey, not stripePriceId
      let lookupKey;
      if (tierId === 'pro') {
        lookupKey = PRO_LOOKUP_KEY;
      } else {
        lookupKey = STARTER_LOOKUP_KEY;
      }

      const payload = {
        lookupKey
      };

      const successUrl = buildReturnUrl("success");
      const cancelUrl = buildReturnUrl("canceled");
      const response = await createCheckoutSession({
        ...payload,
        successUrl,
        cancelUrl
      });
      const redirectUrl = response?.data?.url || response?.url;
      if (!redirectUrl) {
        throw new Error("Missing checkout url");
      }
      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Checkout error", error);
      setPaymentError("Unable to start checkout. Please try again.");
    } finally {
      setIsPaymentProcessing(false);
      setPaymentStep("");
    }
  };

  const handleTopUpCheckout = async (lookupKey, options = {}) => {
    if (typeof window === "undefined") return;
    console.log("[TopUp] Starting checkout with lookupKey:", lookupKey, "source:", options.source);
    try {
      // Set post-checkout action based on source
      // Only open AI Report modal if coming from AI Report flow, not from other sources
      let postAction = 'none';
      if (options.source === 'developer-portal') {
        postAction = 'developer-portal';
      } else if (options.source === 'meeting-assistant') {
        postAction = 'meeting-assistant';
      } else if (!options.source || options.source === 'ai-report') {
        postAction = 'ai-report';
      }
      window.localStorage.setItem("cm_post_checkout_action", postAction);
      window.localStorage.setItem("cm_checkout_type", "topup");

      // Store current view to restore after checkout
      if (options.source === 'developer-portal') {
        window.localStorage.setItem("cm_post_checkout_view", "developer");
      } else if (options.source === 'meeting-assistant') {
        window.localStorage.setItem("cm_post_checkout_view", "assistant");
      }

      if (options.expectedCredits) {
        window.localStorage.setItem("cm_topup_expected", String(options.expectedCredits));
      }
      if (userMeta?.credit != null) {
        const creditValue = Number(String(userMeta.credit).replace(/[^0-9.]/g, ""));
        if (!Number.isNaN(creditValue)) {
          window.localStorage.setItem("cm_topup_previous", String(creditValue));
        }
      }
    } catch { }
    setCheckoutType("topup");
    setPaymentError("");
    setCheckoutMessage("");
    setCheckoutSuccess(false);
    setPaymentStep("Redirecting to Stripe...");
    setIsPaymentProcessing(true);
    try {
      backupTokensForCheckout();
      const successUrl = buildReturnUrl("success");
      const cancelUrl = buildReturnUrl("canceled");
      const payload = {
        lookupKey,
        successUrl,
        cancelUrl
      };
      console.log("[TopUp] Checkout payload:", payload);
      const response = await createCheckoutSession(payload);
      const redirectUrl = response?.data?.url || response?.url;
      if (!redirectUrl) {
        throw new Error("Missing checkout url");
      }
      if (options.openInNewTab) {
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Top up checkout error", error);
      setPaymentError("Unable to start top up checkout. Please try again.");
    } finally {
      setIsPaymentProcessing(false);
      setPaymentStep("");
    }
  };

  const handleOpenPortal = async () => {
    setPaymentError("");
    setCheckoutMessage("");
    setIsPaymentProcessing(true);
    setPaymentStep("Opening billing portal...");
    try {
      const payload = checkoutSessionId ? { sessionId: checkoutSessionId } : {};
      const response = await createPortalSession(payload);
      const portalUrl = response?.data?.url || response?.url;
      if (!portalUrl) {
        throw new Error("Missing portal url");
      }
      window.location.href = portalUrl;
    } catch (error) {
      console.error("Portal error", error);
      setPaymentError(
        checkoutSessionId
          ? "Unable to open billing portal. Please try again."
          : "Unable to open billing portal. Please complete a checkout first."
      );
    } finally {
      setIsPaymentProcessing(false);
      setPaymentStep("");
    }
  };

  const clearStripeQueryParams = () => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.delete("success");
    url.searchParams.delete("canceled");
    url.searchParams.delete("session_id");
    url.searchParams.delete("sessionId");
    window.history.replaceState({}, document.title, url.toString());
  };

  const handleCheckoutSuccessAcknowledge = async () => {
    const email = userEmail || userMeta.email || "";
    if (!email) {
      setIsPaymentOpen(false);
      return;
    }
    setPaymentError("");
    setIsPaymentProcessing(true);
    setPaymentStep("Updating membership...");
    try {
      // Fetch latest subscription from backend using correct endpoint
      console.log("🔄 Fetching fresh subscription data after checkout...");
      const subscriptionResponse = await fetchUserSubscription().catch((error) => {
        console.error("Subscription refresh error", error);
        return null;
      });

      if (subscriptionResponse?.data) {
        const subscription = subscriptionResponse.data;
        console.log("✅ Fresh subscription data received:", subscription);

        // Extract tier and credits from subscription response
        // Handle both flat and nested API response structures
        const tierFromSubscription = subscription?.tier || subscription?.data?.tier || subscription?.plan?.tier || subscription?.plan?.name;
        const resolvedMembershipNormalized = normalizeTier(tierFromSubscription);
        const resolvedMembership = resolvedMembershipNormalized.charAt(0).toUpperCase() + resolvedMembershipNormalized.slice(1);
        const resolvedCredit = subscription?.aiCreditsRemaining ?? subscription?.data?.aiCreditsRemaining ?? 0;

        const nextMeta = {
          membership: resolvedMembership,
          credit: resolvedCredit,
          email: subscription?.userId || subscription?.data?.userId || email
        };

        console.log("📝 Updating userMeta with fresh data:", nextMeta);
        setUserMetaSafe(nextMeta);

        try {
          if (typeof window !== "undefined") {
            window.localStorage.setItem("cm_user_meta", JSON.stringify(nextMeta));
          }
        } catch { }
      }

      await loadDashboard();
      clearStripeQueryParams();
      setIsPaymentOpen(false);
      setCheckoutSuccess(false);
      setCheckoutMessage("");
    } catch (error) {
      console.error("Membership update error", error);
      setPaymentError("Subscription succeeded, but we could not update your membership. Please try again.");
    } finally {
      setIsPaymentProcessing(false);
      setPaymentStep("");
    }
  };

  const handleLoanAmountChange = (event) => {
    const raw = event.target.value || "";
    const numeric = raw.replace(/[^0-9]/g, "");
    const formatted = numeric ? Number(numeric).toLocaleString() : "";
    setCaseForm((prev) => ({ ...prev, loanAmount: formatted }));
  };

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    if (!selectedCase) return;
    const caseIdValue = String(selectedCase.id);
    setIsStatusUpdating(true);
    updateCaseStatus({ caseId: selectedCase.id, newStatus })
      .then((response) => {
        const updatedStatus = typeof response.data?.status === "string" ? response.data.status : newStatus;
        setCases((prev) => {
          const nextCases = prev.map((c) => (String(c.id) === caseIdValue ? { ...c, status: updatedStatus } : c));
          setStats((prevStats) => ({ ...prevStats, ...computeCaseStats(nextCases) }));
          return nextCases;
        });
        setSelectedCase((prev) => (prev ? { ...prev, status: updatedStatus } : prev));

        // Trigger celebration modal for milestone statuses
        if (updatedStatus === "Approved" || updatedStatus === "Cleared to Close") {
          setCelebrationStatus(updatedStatus);
          // Celebration effects will trigger when user clicks Continue button
        }

        loadDashboard();
      })
      .catch((error) => {
        console.error(error);
        setCaseError("Unable to update case status. Please retry.");
      })
      .finally(() => setTimeout(() => setIsStatusUpdating(false), 400));
  };

  const handleCaseUpdate = (updatedCaseData) => {
    const caseIdValue = String(updatedCaseData.id || updatedCaseData.caseId || updatedCaseData.case_id);
    setCases((prev) => {
      const nextCases = prev.map((c) =>
        String(c.id || c.caseId || c.case_id) === caseIdValue ? { ...c, ...updatedCaseData } : c
      );
      setStats((prevStats) => ({ ...prevStats, ...computeCaseStats(nextCases) }));
      return nextCases;
    });
    setSelectedCase((prev) => (prev ? { ...prev, ...updatedCaseData } : prev));
  };

  const handleAddComment = async (event, text = null) => {
    if (event && event.preventDefault) event.preventDefault();
    const textToUse = text !== null ? text : newComment;
    if (!selectedCase || !textToUse.trim()) return;

    setIsCommenting(true);
    setCaseError(""); // Clear any previous errors

    const commentText = textToUse.trim();
    const caseId = selectedCase.id || selectedCase.caseId || selectedCase.case_id;

    try {
      // Call the backend API to persist the comment
      const response = await addCaseComment({
        caseId: caseId,
        text: commentText,
        author: userEmail || "Loan Admin"
      });

      console.log("Add comment API response:", response);

      // Extract the comment from the response - handle various nesting levels
      let savedComment = null;

      // Try different response structures
      if (response?.data?.data?.comment) {
        savedComment = response.data.data.comment;
      } else if (response?.data?.comment) {
        savedComment = response.data.comment;
      } else if (response?.data?.data) {
        savedComment = response.data.data;
      } else if (response?.data) {
        savedComment = response.data;
      }

      console.log("Extracted comment:", savedComment);

      // Ensure we have all required fields
      const normalizedComment = {
        id: savedComment?.id || savedComment?._id || `temp-${Date.now()}`,
        text: savedComment?.text || commentText,
        author: savedComment?.author || savedComment?.user || userEmail || "Loan Admin",
        createdAt: savedComment?.createdAt || savedComment?.created_at || savedComment?.timestamp || new Date().toISOString()
      };

      console.log("Normalized comment:", normalizedComment);

      // Update local state with the saved comment
      setCaseComments((prev) => {
        const existing = prev[selectedCase.id] || [];
        return { ...prev, [selectedCase.id]: [normalizedComment, ...existing] };
      });

      setNewComment("");
    } catch (error) {
      console.error("Failed to add comment:", error);
      setCaseError(error?.message || "Unable to add comment. Please try again.");
    } finally {
      setTimeout(() => {
        setIsCommenting(false);
      }, 500);
    }
  };

  const resolveFileLink = async (file) =>
    resolveFileLinkHelper(file, selectedCase, fetchSignedUrl, fetchCaseById);

  const handlePreview = async (file) => {
    setIsPreviewLoading(true);
    setCaseError(""); // Clear any previous errors

    try {
      if (!file) return;

      console.log("handlePreview called with:", file);

      // Normalize chat attachment keys to standard objectKey
      let objectKey = file.objectKey || file.s3_key || file.object_key;

      // If we still don't have an objectKey but have a selected case, try to find the file in the case documents
      if (!objectKey && selectedCase) {
        const currentFiles = caseFiles[selectedCase.id] || selectedCase.documents || [];
        const latestFile = currentFiles.find(f =>
          f.name === file.name ||
          f.fileName === file.name ||
          f.originalName === file.name
        );
        if (latestFile && latestFile.objectKey) {
          objectKey = latestFile.objectKey;
          // Merge properties from the found file to ensure we better metadata
          file = { ...latestFile, ...file };
        }
      }

      let link = null;

      // 1. Try to get a fresh signed URL from API if we have an objectKey
      // This logic mirrors LenderChat.js exactly
      if (objectKey) {
        try {
          console.log("📥 Fetching signed URL for objectKey:", objectKey);
          // Use the imported fetchSignedUrl directly
          const response = await fetchSignedUrl(objectKey);
          // Check all possible return paths as per LenderChat.js
          link = response?.data?.url || response?.data?.signedUrl || response?.url || response?.signedUrl;
          console.log("✅ Got signed URL from API:", link);
        } catch (error) {
          console.error("❌ Failed to fetch signed URL:", error);
        }
      }

      // 2. Fallback to existing properties if API fetch failed or no objectKey
      if (!link) {
        link = file.signed_url || file.url || file.signedUrl || file.previewUrl || file.downloadUrl;
      }

      // 3. Last resort fallback (using the helper if available, but logic above should cover most cases)
      if (!link && objectKey) {
        try {
          link = await resolveFileLink(file);
        } catch (e) {
          console.warn("Helper resolution also failed");
        }
      }

      if (!link) {
        setCaseError("Preview not available for this file.");
        return;
      }

      // Resolve name aggressively
      const docName = file.name || file.filename || file.fileName || file.originalName || file.original_filename || "Untitled Document";

      setPreviewDoc({
        name: docName,
        url: link,
        type: file.type || file.file_type || file.contentType || "application/pdf",
        isObjectUrl: !!file.isObjectUrl,
        canSave: Boolean(file.canSave),
        saveName: file.saveName || docName
      });
    } catch (error) {
      console.error("Error loading document preview:", error);
      setCaseError("Unable to load document preview. Please try again.");
    } finally {
      setTimeout(() => setIsPreviewLoading(false), 150);
    }
  };

  const handleSavePreview = async ({ name, url, type }) => {
    if (!selectedCase || !name || !url) return false;
    setIsPreviewSaving(true);
    setCaseError("");
    try {
      const normalizedName = name.trim();
      const needsPdfExt = type === "application/pdf" && !normalizedName.toLowerCase().endsWith(".pdf");
      const fileName = needsPdfExt ? `${normalizedName}.pdf` : normalizedName;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Unable to fetch file (${response.status})`);
      }
      const buffer = await response.arrayBuffer();
      if (!buffer || buffer.byteLength === 0) {
        throw new Error("Downloaded file is empty.");
      }
      if ((type || "").includes("pdf")) {
        const header = new TextDecoder("ascii").decode(buffer.slice(0, 4));
        if (header !== "%PDF") {
          throw new Error("File is not a valid PDF.");
        }
      }
      const mimeType = type || "application/pdf";
      const file = new File([buffer], fileName, { type: mimeType });
      const validation = validateFiles([file]);
      if (!validation.valid) {
        setCaseError("Upload failed. Please check your file and try again.");
        return false;
      }

      const sanitizedFiles = validation.validFiles.map(sanitizeFile);
      const existingNames = new Set((caseFiles[selectedCase.id] || []).map((f) => (f.name || "").toLowerCase()));
      const duplicates = sanitizedFiles.filter((incoming) => existingNames.has((incoming.name || "").toLowerCase()));
      if (duplicates.length > 0) {
        setPendingUploadFiles(sanitizedFiles);
        setDuplicateFiles(duplicates.map((incoming) => incoming.name));
        setIsDuplicatePromptOpen(true);
        return false;
      }

      const arrayBufferToBase64 = (arrBuffer) => {
        let binary = "";
        const bytes = new Uint8Array(arrBuffer);
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
        }
        return btoa(binary);
      };

      setIsUploading(true);
      setUploadProgress(10);
      const base64 = arrayBufferToBase64(buffer);
      const filePayloads = [
        {
          name: file.name,
          size: file.size,
          type: mimeType,
          content: base64
        }
      ];

      setUploadProgress(60);
      const responseUpload = await uploadCaseDocuments({ caseId: selectedCase.id, files: filePayloads });
      setUploadProgress(100);
      const serverDocs = Array.isArray(responseUpload.data)
        ? responseUpload.data
        : Array.isArray(responseUpload.data?.documents)
          ? responseUpload.data.documents
          : Array.isArray(responseUpload.data?.files)
            ? responseUpload.data.files
            : [];
      const fallbackDocs =
        serverDocs.length > 0
          ? []
          : [
            {
              name: file.name,
              size: file.size,
              type: mimeType,
              source: "Uploaded",
              uploadedAt: new Date().toISOString(),
              previewUrl: URL.createObjectURL(new Blob([buffer], { type: mimeType })),
              isObjectUrl: true
            }
          ];
      const uploadedDocs = serverDocs.map((doc) => ({
        name: doc.name,
        size: doc.size,
        type: doc.type || doc.mimeType || "application/pdf",
        source: doc.source || "Uploaded",
        uploadedAt: doc.uploadedAt || new Date().toISOString(),
        previewUrl: doc.signedUrl || doc.previewUrl || doc.downloadUrl || null,
        downloadUrl: doc.signedUrl || doc.downloadUrl || doc.previewUrl || null,
        signedUrl: doc.signedUrl || null,
        objectKey: doc.objectKey || null
      }));
      const updatedDocuments = [...uploadedDocs, ...fallbackDocs, ...(selectedCase.documents || [])];
      setCaseFiles((prev) => ({
        ...prev,
        [selectedCase.id]: updatedDocuments
      }));
      setSelectedCase((prev) => ({
        ...prev,
        documents: updatedDocuments
      }));
      setUploadMessage("Document uploaded successfully.");

      await new Promise((resolve) => setTimeout(resolve, 1500));
      await loadDashboard();
      if (selectedCase?.id) {
        const refreshedCases = await loadDashboard();
        const currentCase = refreshedCases.find(c => c.id === selectedCase.id);
        if (currentCase) {
          await handleCaseSelect(currentCase);
        }
      }
      return true;
    } catch (error) {
      console.error("Preview save failed", error);
      setCaseError("Unable to save document. Please try again.");
      return false;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setIsPreviewSaving(false);
    }
  };

  const handleDeleteFile = (file) => {
    if (!selectedCase || !file) return;
    setDeleteCandidate(file);
    setFileDeleteTarget(file.name);
  };

  const confirmDeleteFile = async () => {
    if (!selectedCase || !deleteCandidate) return;
    const objectKey = deleteCandidate.objectKey;
    if (!objectKey) {
      alert("Cannot delete: Missing object key");
      setDeleteCandidate(null);
      setFileDeleteTarget(null);
      return;
    }

    setIsDeletingFile(true);
    try {
      // Call the real delete API
      await deleteDocument(objectKey);

      // Remove from local state
      setCaseFiles((prev) => {
        const existing = prev[selectedCase.id] || [];
        const filtered = existing.filter((f) => f.objectKey !== objectKey);
        return { ...prev, [selectedCase.id]: filtered };
      });

      // Refresh case data to ensure consistency
      await loadDashboard();
      if (selectedCase?.id) {
        const refreshedCases = await loadDashboard();
        const currentCase = refreshedCases.find(c => c.id === selectedCase.id);
        if (currentCase) {
          await handleCaseSelect(currentCase);
        }
      }
    } catch (error) {
      console.error("Delete document failed:", error);
      alert("Failed to delete document. Please try again.");
    } finally {
      setTimeout(() => {
        setIsDeletingFile(false);
        setDeleteCandidate(null);
        setFileDeleteTarget(null);
      }, 300);
    }
  };

  const cancelDelete = () => {
    setDeleteCandidate(null);
    setFileDeleteTarget(null);
  };

  const closePreview = () => {
    if (previewDoc?.isObjectUrl) {
      URL.revokeObjectURL(previewDoc.url);
    }
    setPreviewDoc(null);
    setIsPreviewLoading(false);
  };

  const handleAiGenerateClick = () => {
    setIsAiConfirmOpen(true);
  };

  const formatRunLabel = (runId, startedAt) => {
    if (runId) {
      const match = String(runId).match(/^run-(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/);
      if (match) {
        const [, year, month, day, hour, minute, second] = match;
        return `Run ${year}-${month}-${day} ${hour}:${minute}:${second}`;
      }
    }
    if (startedAt) {
      const date = new Date(startedAt);
      if (!isNaN(date.getTime())) {
        const pad = (value) => String(value).padStart(2, "0");
        const label = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        return `Run ${label}`;
      }
    }
    return "Run";
  };

  const handleAiConfirm = (config) => {
    const selectedCaseKey = selectedCase?.id || selectedCase?.caseId || selectedCase?.case_id;
    if (!selectedCaseKey) return;

    // Prevent duplicate requests if already generating
    if (isAiGenerating) {
      console.warn("AI report generation already in progress, ignoring duplicate request");
      return;
    }

    const reportTier = config.reportType || "basic";
    const reportCost = reportTier === "advanced" ? 1000 : 150;
    const acceleration = config.acceleration;
    const accelerationCost = acceleration?.credits || 0;
    const totalCost = config.totalCredits || (reportCost + accelerationCost);

    // Calculate delivery hours based on acceleration or standard tier
    const deliveryHours = acceleration?.hours || (userMeta?.membership ?
      ({ free: 9, starter: 6, pro: 3, ultra: 1 }[userMeta.membership.toLowerCase()] || 9) : 9);

    setIsAiGenerating(true);
    setIsAiConfirmOpen(false);
    setCaseError(""); // Clear any previous errors
    setAiMessage(""); // Clear any previous messages

    console.log("Requesting AI report for case:", selectedCaseKey, {
      reportTier,
      reportCost,
      acceleration: acceleration?.level,
      accelerationCost,
      deliveryHours,
      totalCost,
      currentCredits: userMeta?.credit
    });
    console.log(`[Credit Calculation] Report: ${reportCost} + Acceleration: ${accelerationCost} = Total: ${totalCost} credits`);



    const requestPayload = {
      caseId: selectedCaseKey,
      membership: userMeta?.membership,
      reportType: config.reportType,
      delivery: config.acceleration?.level || "standard",
      totalCost: totalCost
    };
    console.log("[AI Report] POST /api/cases/ai-report payload:", requestPayload);

    requestAiReport(requestPayload)
      .then((response) => {
        console.log("AI report API response:", response);

        // Handle nested response structure: response.data.data or response.data
        const responseData = response.data?.data || response.data;
        console.log("responseData:", responseData);

        const responseStatus = response?.status;
        // Check if the request was successful with new API format
        if (responseData?.requestId) {
          const requestId = responseData.requestId;
          const status = responseData.status; // "RUNNING", "SUCCEEDED", "FAILED", etc.
          const startedAt = responseData.startedAt;
          const trackingUrl = responseData.trackingUrl;

          // Calculate ETA based on selected delivery time
          const etaDate = new Date();
          etaDate.setHours(etaDate.getHours() + deliveryHours);
          const etaDisplay = etaDate.toLocaleString();

          const resolvedStatus = status || "PENDING";
          const accelerationMsg = acceleration ? ` (${acceleration.label} - ${deliveryHours}h delivery)` : ` (${deliveryHours}h delivery)`;
          setAiMessage(
            `AI report generation (${reportTier === "advanced" ? "Advanced Report" : "Basic"})${accelerationMsg} started successfully! Request ID: ${requestId}. ` +
            `Status: ${resolvedStatus}. Expected completion: ${etaDisplay}`
          );
          setPipelineStatus({
            status: resolvedStatus,
            run_id: requestId,
            started_at: startedAt || new Date().toISOString()
          });
          if (responseStatus === 202 && trackingUrl) {
            startRunPolling(trackingUrl);
          }

          // Backend already deducts credits, just refresh to get updated balance
          // DO NOT call deductSubscriptionCredits here - it causes double deduction!
          const creditsBefore = userMeta?.credit || 0;
          console.log(`[AI Report] Credits before: ${creditsBefore}, deducting: ${totalCost}`);
          console.log(`[AI Report] Expected credits after: ${creditsBefore - totalCost}`);
          console.log("[AI Report] Backend has deducted credits, refreshing subscription data...");
          refreshSubscriptionMeta()
            .then(() => {
              console.log(`[AI Report] Credits after refresh: ${userMeta?.credit || 0}`);
            })
            .catch((refreshError) => {
              console.error("Failed to refresh subscription after AI report request:", refreshError);
            });

          // Add to AI reports section (NOT to caseFiles)
          setAiReports((prev) => ({
            ...prev,
            [selectedCaseKey]: [
              ...(prev[selectedCaseKey] || []),
              {
                id: requestId,
                status: status,
                eta: etaDisplay,
                name: formatRunLabel(requestId, startedAt),
                url: null,
                trackingUrl: trackingUrl,
                startedAt: startedAt || new Date().toISOString()
              }
            ]
          }));
        } else {
          // API didn't return expected format
          console.warn("API did not return expected format. Full response:", response);
          const errorDetail = responseData?.error || responseData?.message || "Unknown error";
          setCaseError(
            `AI report generation could not be started: ${errorDetail}. Please check your case documents and try again.`
          );
        }
      })
      .catch((error) => {
        console.error("AI report generation error:", error);

        if (error.status === 429 && error.data?.detail) {
          const detail = error.data.detail;

          if (detail.existing_status) {
            setCaseError(`A pipeline is already processing this case. Please wait for it to finish.`);
          } else if (detail.cooldown_remaining_seconds) {
            setCaseError(`Please wait ${detail.cooldown_remaining_seconds} seconds before starting another pipeline run.`);
          } else {
            setCaseError(detail.message || "Rate limit exceeded. Please try again later.");
          }
        } else {
          setCaseError(
            error.message || "Unable to generate AI report. Please ensure all documents are uploaded and try again."
          );
        }
      })
      .finally(() => {
        setIsAiGenerating(false);
        // Clear the success message after 10 seconds
        setTimeout(() => setAiMessage(""), 10000);
      });
  };
  return (
    <div className="h-screen dark:bg-slate-950 bg-gray-50 dark:text-slate-100 text-gray-900 font-sans flex relative">
      {showInitialLoader && (
        <div className="absolute inset-0 z-50 flex items-center justify-center dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur">
          <div className="dark:bg-slate-900/80 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl px-6 py-4 shadow-lg flex items-center gap-3">
            <Loader2 className="w-5 h-5 dark:text-yellow-400 text-yellow-600 animate-spin" />
            <div>
              <p className="text-sm font-semibold dark:text-white text-slate-900">Loading your dashboard</p>
              <p className="text-xs text-slate-500">Fetching latest cases...</p>
            </div>
          </div>
        </div>
      )}
      {isRefreshing && !showInitialLoader && (
        <div className="fixed top-4 right-4 z-40 dark:bg-slate-900/90 bg-white border dark:border-slate-800 border-slate-200 rounded-full px-3 py-1.5 shadow flex items-center gap-2 text-xs dark:text-slate-200 text-slate-700">
          <Loader2 className="w-4 h-4 animate-spin dark:text-yellow-400 text-yellow-600" />
          <span>Refreshing</span>
        </div>
      )}

      {/* Processing / Loading notification for checkout */}
      {!checkoutSuccess && checkoutMessage && (checkoutMessage.includes("Verifying") || checkoutMessage.includes("Finalizing")) && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] bg-indigo-900/95 border border-indigo-500 rounded-xl px-6 py-4 shadow-2xl backdrop-blur-md flex items-center gap-4 text-sm animate-in fade-in slide-in-from-top-4 duration-500 min-w-[320px]">
          <Loader2 className="w-5 h-5 text-indigo-300 animate-spin" />
          <div>
            <p className="font-semibold text-white text-base">Processing Payment</p>
            <p className="text-xs text-indigo-200 mt-0.5">{checkoutMessage}</p>
          </div>
        </div>
      )}

      {/* Success notification after credit purchase */}
      {/* Success notification after credit purchase */}
      {checkoutSuccess && checkoutMessage && checkoutType === "topup" && !isPaymentProcessing && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] bg-emerald-900/95 border border-emerald-600 rounded-xl px-6 py-4 shadow-2xl backdrop-blur-md flex items-center gap-4 text-sm animate-in fade-in slide-in-from-top-4 duration-500 min-w-[320px] justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
            <div>
              <p className="font-semibold text-white text-base">Payment Successful!</p>
              <p className="text-xs text-emerald-200 mt-0.5">
                Thank you for your purchase. Balance: {userMeta?.credit?.toLocaleString() || 0} credits
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setCheckoutSuccess(false);
              setCheckoutMessage("");
            }}
            className="ml-2 text-emerald-300 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Error notification for credit purchase failure */}
      {!checkoutSuccess && checkoutMessage && !checkoutMessage.includes("Verifying") && !checkoutMessage.includes("Finalizing") && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] bg-rose-950/95 border border-rose-600 rounded-xl px-6 py-4 shadow-2xl backdrop-blur-md flex items-center gap-4 text-sm animate-in fade-in slide-in-from-top-4 duration-500 min-w-[320px] justify-between">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-2 h-2 rounded-full bg-rose-500 animate-pulse"></div>
            <div>
              <p className="font-semibold text-white text-base">
                {checkoutMessage.toLowerCase().includes("cancel") ? "Purchase Canceled" : "Update Failed"}
              </p>
              <p className="text-xs text-rose-200 mt-0.5">
                {checkoutMessage}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setCheckoutSuccess(true); // Reset to hide error
              setCheckoutMessage("");
            }}
            className="ml-2 text-rose-300 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {isCaseLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/60 bg-slate-500/50 backdrop-blur-sm">
          <div className="dark:bg-slate-900/95 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl px-8 py-6 shadow-2xl flex items-center gap-4">
            <Loader2 className="w-6 h-6 dark:text-yellow-400 text-yellow-600 animate-spin" />
            <div>
              <p className="text-base font-semibold dark:text-white text-slate-900">Loading case details</p>
              <p className="text-sm text-slate-400">Fetching documents...</p>
            </div>
          </div>
        </div>
      )}
      {isAiGenerating && !isAiConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/60 bg-slate-500/50 backdrop-blur-sm">
          <div className="dark:bg-slate-900/95 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl px-8 py-6 shadow-2xl flex items-center gap-4">
            <Loader2 className="w-6 h-6 dark:text-yellow-400 text-yellow-600 animate-spin" />
            <div>
              <p className="text-base font-semibold dark:text-white text-slate-900">Generating AI Report</p>
              <p className="text-sm text-slate-400">Starting Step Functions execution...</p>
            </div>
          </div>
        </div>
      )}


      <Sidebar
        openNewCaseModal={openNewCaseModal}
        onNavigate={setCurrentView}
        currentView={currentView}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        featureFlags={featureFlags}
        unreadChatCount={unreadChatCount}
      />

      <main className="flex-1 flex flex-col min-w-0">
        <DashboardHeader
          userEmail={userEmail}
          userMeta={userMeta}
          isProfileOpen={isProfileOpen}
          setIsProfileOpen={setIsProfileOpen}
          profileMenuRef={profileMenuRef}
          onLogout={onLogout}
          setIsAccountSettingsOpen={setIsAccountSettingsOpen}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearchSubmit={handleSearchSubmit}
          openNewCaseModal={openNewCaseModal}
          onMenuClick={() => setIsSidebarOpen(true)}
        />

        <div className={`p-6 flex-1 min-h-0 ${currentView === "assistant" || currentView === "chat" || currentView === "marketing" ? "overflow-hidden flex flex-col" : "overflow-auto"}`}>
          {caseError && (
            <div className="mb-4 text-sm text-yellow-300 bg-yellow-500/10 border border-yellow-500/40 rounded-lg px-4 py-3">
              {caseError}
            </div>
          )}
          {currentView === 'cases' && (
            <>
              <DashboardStats stats={stats} cases={cases} setStatusFilter={setStatusFilter} statusFilter={statusFilter} />

              <div className="flex justify-between items-center mb-4">
                <h2 className="dark:text-white text-slate-900 font-bold">Recent Files</h2>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={sortConfig.key}
                      onChange={(e) => setSortConfig({ ...sortConfig, key: e.target.value })}
                      className="dark:bg-slate-800 bg-white dark:text-slate-300 text-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 border dark:border-slate-700 border-slate-200"
                    >
                      <option value="date">Sort by Date</option>
                      <option value="name">Sort by Name</option>
                      <option value="loanAmount">Sort by Loan Amount</option>
                    </select>
                    <button
                      onClick={() =>
                        setSortConfig({
                          ...sortConfig,
                          direction: sortConfig.direction === "ascending" ? "descending" : "ascending",
                        })
                      }
                      className="p-2 rounded-lg dark:bg-slate-800 bg-white dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 border dark:border-slate-700 border-slate-200"
                    >
                      {sortConfig.direction === "ascending" ? "↑" : "↓"}
                    </button>
                  </div>
                  <button
                    onClick={() => setViewMode("table")}
                    className={`p-2 rounded-lg transition-colors border dark:border-transparent border-slate-200 ${viewMode === "table" ? "bg-yellow-600 text-white border-transparent" : "dark:bg-slate-800 bg-white dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                      }`}
                  >
                    <Rows className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode("kanban")}
                    className={`p-2 rounded-lg transition-colors border dark:border-transparent border-slate-200 ${viewMode === "kanban" ? "bg-yellow-600 text-white border-transparent" : "dark:bg-slate-800 bg-white dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                      }`}
                  >
                    <LayoutGrid className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
          {currentView === "teams" && (
            <div className="flex items-center justify-between mb-4">
              {/* TeamsManager handles its own header */}
            </div>
          )}
          {currentView === "collab-cases" && (
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="dark:text-white text-slate-900 font-bold">Shared Cases</h2>
                <p className="text-sm text-slate-400">Cases shared with you by teammates.</p>
              </div>
            </div>
          )}
          {currentView === "chat" && (
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="dark:text-white text-slate-900 font-bold">Team Chat</h2>
                <p className="text-sm text-slate-400">Communicate with your team members in real-time.</p>
              </div>
            </div>
          )}
          {currentView === "assistant" && (
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="dark:text-white text-slate-900 font-bold">A.I Assistant</h2>
                {/* Tabs */}
                <div className="flex items-center gap-1 mt-2">
                  <button
                    onClick={() => setAssistantTab("case")}
                    className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${assistantTab === "case"
                      ? "bg-yellow-500 text-white"
                      : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                  >
                    Case Assistant
                  </button>
                  <button
                    onClick={() => setAssistantTab("meeting")}
                    className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${assistantTab === "meeting"
                      ? "bg-yellow-500 text-white"
                      : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                  >
                    Meeting Assistant
                  </button>
                </div>
              </div>
            </div>
          )}
          <AnimatePresence mode="wait">
            <motion.div
              key={viewMode}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className={currentView === "assistant" || currentView === "chat" || currentView === "training" || currentView === "support" || currentView === "tasks" || currentView === "affiliate" || currentView === "developer" || currentView === "notes" || currentView === "calendar" || currentView === "marketing" ? "flex-1 flex flex-col min-h-0" : ""}
            >
              {currentView === 'cases' ? (
                showInitialLoader ? (
                  <CasesSkeleton />
                ) : viewMode === 'table' ? (
                  <CasesTable
                    cases={cases}
                    filteredBorrowers={filteredBorrowers}
                    paginatedBorrowers={paginatedBorrowers}
                    sortConfig={sortConfig}
                    handleSort={handleSort}
                    handleCaseSelect={handleCaseSelect}
                    openNewCaseModal={openNewCaseModal}
                    searchTerm={searchTerm}
                  />
                ) : (
                  <KanbanBoard cases={filteredBorrowers} onStatusChange={handleKanbanStatusChange} onCaseSelect={handleCaseSelect} />
                )
              ) : currentView === "teams" ? (
                <TeamsManager
                  userEmail={userEmail}
                  subscription={subscriptionData}
                  onUpgradePlan={() => setIsAccountSettingsOpen(true)}
                  onCaseSelect={handleCaseSelect}
                  initialMembers={teamMembers}
                  isPreloaded={isTeamPreloaded}
                />
              ) : currentView === "collab-cases" ? (
                <CollabCasesList
                  cases={filteredCollabCases}
                  isLoading={isCollabCasesLoading}
                  error={collabCasesError}
                  onCaseSelect={handleCaseSelect}
                />
              ) : currentView === "chat" ? (
                <TeamChat
                  userEmail={userEmail}
                  initialConversations={chatConversations}
                  isPreloaded={isChatPreloaded}
                  onPreview={handlePreview}
                  featureFlags={featureFlags}
                  subscription={subscriptionData}
                  onUpgradePlan={handleOpenPortal}
                  onUnreadCountChange={setUnreadChatCount}
                />
              ) : currentView === "assistant" ? (
                assistantTab === "case" ? (
                  <LenderChat
                    cases={cases}
                    caseFiles={caseFiles}
                    onOpenNewCase={openNewCaseModal}
                    onUpdateCaseFiles={setCaseFiles}
                    isLoading={showInitialLoader}
                  />
                ) : (
                  <MeetingAssistant
                    userCredits={userMeta?.credit || 0}
                    onRefreshCredits={refreshSubscriptionMeta}
                    creditPacks={creditPacks}
                    onTopUp={(value) => {
                      if (typeof value === "object" && value) {
                        const lookup = value.lookupKey;
                        const credits = Number(String(value.credits || "").replace(/[^0-9]/g, ""));
                        if (lookup && lookup.startsWith("credits_")) {
                          handleTopUpCheckout(lookup, {
                            openInNewTab: Boolean(value.openInNewTab),
                            expectedCredits: credits || undefined,
                            source: 'meeting-assistant'
                          });
                        }
                      }
                    }}
                    isPaymentProcessing={isPaymentProcessing}
                  />
                )
              ) : currentView === "training" ? (
                <TrainingPortal />
              ) : currentView === "tasks" ? (
                <TasksDashboard
                  onNavigateToCase={(caseId) => {
                    const match = cases.find(c => c.id === caseId || c.caseId === caseId);
                    if (match) handleCaseSelect(match);
                  }}
                  currentUserEmail={userEmail}
                />
              ) : currentView === "support" ? (
                <SupportPage userEmail={userEmail} />
              ) : currentView === "developer" ? (
                <DeveloperPortal
                  userMeta={userMeta}
                  onUpgradePlan={handleStartCheckout}
                  onTopUp={handleTopUpCheckout}
                  creditPacks={creditPacks}
                  isPaymentProcessing={isPaymentProcessing}
                />
              ) : currentView === "affiliate" ? (
                <AffiliatePortal userMeta={userMeta} />
              ) : currentView === "calendar" ? (
                <CalendarPage
                  currentUserEmail={userEmail}
                  cases={cases}
                />
              ) : currentView === "notes" ? (
                <NotesPage
                  currentUserEmail={userEmail}
                  onNavigateToCase={(caseId) => {
                    const match = cases.find(c => c.id === caseId || c.caseId === caseId);
                    if (match) handleCaseSelect(match);
                  }}
                  cases={cases}
                />
              ) : currentView === "marketing" ? (
                <SocialMediaManager
                  userEmail={userEmail}
                  subscription={subscriptionData}
                  featureFlags={featureFlags}
                  onUpgradePlan={handleStartCheckout}
                />
              ) : (
                <div className="flex flex-col h-full bg-white dark:bg-slate-950 overflow-y-auto">
                  <div className="px-8 pt-6 pb-0 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 flex items-center gap-8 sticky top-0 z-20">
                    <button
                      onClick={() => setDashboardTab('overview')}
                      className={`pb-4 text-sm font-bold uppercase tracking-wider transition-all border-b-2 ${dashboardTab === 'overview'
                        ? 'border-yellow-500 text-slate-900 dark:text-white'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                    >
                      Executive Overview
                    </button>
                    <button
                      onClick={() => setDashboardTab('tasks')}
                      className={`pb-4 text-sm font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-2 ${dashboardTab === 'tasks'
                        ? 'border-yellow-500 text-slate-900 dark:text-white'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                    >
                      Tasks Dashboard
                    </button>
                  </div>

                  <div className="flex-1 relative">
                    {dashboardTab === 'overview' ? (
                      <LenderPerformanceDashboard
                        dashboardData={transformCasesToDashboardData(cases, portfolioPnlSummary)}
                        pnlLoading={isPnlSummaryLoading}
                        pnlError={pnlSummaryError}
                        isCaseLoading={isCaseLoading}
                        onAlertSelect={(alert) => {
                          const caseId = alert?.caseId;
                          const caseNumber = alert?.caseNumber;
                          const match = cases.find((item) =>
                            (caseId && (item.id === caseId || item.caseId === caseId || item.case_id === caseId)) ||
                            (caseNumber && item.caseNumber === caseNumber)
                          );
                          if (match) {
                            handleCaseSelect(match);
                          }
                        }}
                      />
                    ) : (
                      <TasksDashboard
                        onNavigateToCase={(caseId) => {
                          const match = cases.find(c => c.id === caseId || c.caseId === caseId);
                          if (match) handleCaseSelect(match);
                        }}
                        currentUserEmail={userEmail}
                      />
                    )}
                  </div>
                </div>
              )
              }
            </motion.div >
          </AnimatePresence >

          {/* Pagination */}
          {
            currentView === "cases" && filteredBorrowers.length > pageSize && (
              <div className="flex items-center justify-between mt-4 text-sm dark:text-slate-300 text-slate-600">
                <div>
                  Showing {Math.min(filteredBorrowers.length, (safePage - 1) * pageSize + 1)}-
                  {Math.min(filteredBorrowers.length, safePage * pageSize)} of {filteredBorrowers.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className={`px-3 py-1 rounded-lg border text-sm transition-colors ${safePage === 1
                      ? "opacity-50 cursor-not-allowed border-slate-200 text-slate-400"
                      : "dark:border-slate-700 dark:text-slate-200 border-slate-200 text-slate-700 hover:border-yellow-500"
                      }`}
                  >
                    Prev
                  </button>
                  <span className="px-2 text-xs dark:text-slate-500 text-slate-400">Page {safePage} of {totalPages}</span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className={`px-3 py-1 rounded-lg border text-sm transition-colors ${safePage === totalPages
                      ? "opacity-50 cursor-not-allowed border-slate-200 text-slate-400"
                      : "dark:border-slate-700 dark:text-slate-200 border-slate-200 text-slate-700 hover:border-yellow-500"
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )
          }
        </div >
      </main >

      <NewCaseModal
        isNewCaseOpen={isNewCaseOpen}
        setIsNewCaseOpen={setIsNewCaseOpen}
        handleCaseSubmit={handleCaseSubmit}
        caseForm={caseForm}
        setCaseForm={setCaseForm}
        handleLoanAmountChange={handleLoanAmountChange}
        isSubmitting={isSubmitting}
        newCaseError={newCaseError}
        theme={theme}
      />

      <AccountSettingsModal
        isAccountSettingsOpen={isAccountSettingsOpen}
        setIsAccountSettingsOpen={setIsAccountSettingsOpen}
        userEmail={userEmail}
        userMeta={userMeta}
        handleLogout={onLogout}
        onUpdateMeta={setUserMetaSafe}
        onUpgradePlan={handleStartCheckout}
        onOpenBillingPortal={handleOpenPortal}
        isPaymentProcessing={isPaymentProcessing}
      />

      <ReportGenerationModal
        isOpen={isAiConfirmOpen}
        onClose={() => setIsAiConfirmOpen(false)}
        onConfirm={handleAiConfirm}
        onTopUp={(value) => {
          if (typeof value === "object" && value) {
            const lookup = value.lookupKey;
            const credits = Number(String(value.credits || "").replace(/[^0-9]/g, ""));
            if (lookup && lookup.startsWith("credits_")) {
              handleTopUpCheckout(lookup, {
                openInNewTab: Boolean(value.openInNewTab),
                expectedCredits: credits || undefined
              });
              return;
            }
            if (credits) {
              value = credits;
            } else {
              console.error("[TopUp] Invalid top up value:", value);
              setPaymentError("Unable to start top up checkout. Missing product configuration.");
              return;
            }
          }
          if (typeof value === "string") {
            if (value.startsWith("credits_")) {
              handleTopUpCheckout(value);
              return;
            }
            const parsedCredits = Number(String(value).replace(/[^0-9]/g, ""));
            if (parsedCredits) {
              value = parsedCredits;
            } else {
              console.error("[TopUp] Invalid top up value:", value);
              setPaymentError("Unable to start top up checkout. Missing product configuration.");
              return;
            }
          }
          const credits = Number(value);
          const packFromApi = creditPacks.find((pack) => Number(String(pack.credits).replace(/[^0-9]/g, "")) === credits);
          const lookupMap = {
            2000: TOPUP_2000_LOOKUP_KEY,
            6000: TOPUP_6000_LOOKUP_KEY,
            30000: TOPUP_30000_LOOKUP_KEY
          };
          const lookupKey = packFromApi?.stripeLookupKey || lookupMap[credits];
          console.log("[TopUp] resolve lookupKey:", { credits, lookupKey, packFromApi });
          if (lookupKey) {
            handleTopUpCheckout(lookupKey);
          } else {
            console.error("[TopUp] Missing lookupKey for credits:", credits);
            setPaymentError("Unable to start top up checkout. Missing product configuration.");
          }
        }}
        creditPacks={creditPacks}
        userTier={userMeta?.membership || 'free'}
        userCredits={userMeta?.credit || 0}
        isGenerating={isAiGenerating}
        isPaymentProcessing={isPaymentProcessing}
      />

      {/* OLD MODAL - TO BE REMOVED */}
      {
        false && isAiConfirmOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/80 px-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-3xl w-full shadow-2xl p-6 space-y-5 max-h-[85vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Choose Report Type</p>
                  <p className="text-lg font-bold text-white">Generate A.I. Underwriting Report</p>
                </div>
                <button onClick={() => setIsAiConfirmOpen(false)} className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-slate-800 rounded-2xl p-5 bg-slate-950/60 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-800/70 border border-slate-700 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-200" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Basic</h3>
                      <p className="text-sm text-slate-400">Cost: 150 Credits</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300">
                    Best for W2 employees with straightforward banking. The AI rapidly extracts data, calculates standard income, and checks for basic sufficient funds.
                  </p>
                  <div className="text-sm text-slate-300 space-y-1">
                    <p>✅ Extracts Income & Assets</p>
                    <p>✅ Basic DTI Calculation</p>
                    <p>✅ Document Data Validation</p>
                  </div>
                  <p className="text-xs text-slate-400">Best For: W2 Borrowers, Refinances, Clean Bank Statements.</p>
                  <button
                    onClick={() => handleAiConfirm({ reportType: "basic" })}
                    className="w-full px-4 py-2 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors"
                  >
                    Choose Basic
                  </button>
                </div>
                <div className="border border-emerald-500/40 rounded-2xl p-5 bg-gradient-to-br from-emerald-500/10 via-slate-950/80 to-slate-950/60 shadow-[0_0_30px_rgba(16,185,129,0.15)] space-y-3 relative">

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-emerald-200" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Advanced Report</h3>
                      <p className="text-sm text-slate-400">Cost: 1,000 Credits</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300">
                    An audit-grade analysis using our advanced Reasoning Engine. It thinks like a human underwriter to resolve discrepancies, cross-reference tax schedules against bank deposits, and draft Letters of Explanation.
                  </p>
                  <div className="text-sm text-slate-300 space-y-1">
                    <p>✅ Complex Income Analysis: (Schedule C, K-1, Corp)</p>
                    <p>✅ Forensic Asset Sourcing: Matches deposits to donor patterns.</p>
                    <p>✅ Logic Checks: Flags "silent" risks other tools miss.</p>
                  </div>
                  <p className="text-xs text-slate-400">Best For: Self-Employed, Investors (REO), Jumbo Loans, "Messy" File.</p>
                  <button
                    onClick={() => handleAiConfirm({ reportType: "advanced" })}
                    className="w-full px-4 py-2 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-400 transition-colors shadow-[0_10px_30px_rgba(16,185,129,0.25)]"
                  >
                    Choose Advanced Report
                  </button>
                </div>
              </div>
              <div className="border-t border-slate-800 pt-4">
                <button
                  type="button"
                  onClick={() => setIsAiFaqOpen((prev) => !prev)}
                  className="w-full flex items-center justify-between gap-3 text-left"
                >
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">FAQs</p>
                  <ArrowRight
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isAiFaqOpen ? "rotate-90 text-yellow-400" : ""}`}
                  />
                </button>
                {isAiFaqOpen && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    {[
                      { question: "Question 1", answer: "Answer 1" },
                      { question: "Question 2", answer: "Answer 2" },
                      { question: "Question 3", answer: "Answer 3" },
                      { question: "Question 4", answer: "Answer 4" },
                    ].map((faq, idx) => {
                      const isOpen = aiFaqOpenIndex === idx;
                      return (
                        <div
                          key={faq.question}
                          className="rounded-xl border border-slate-800 bg-slate-900/70 shadow-md shadow-black/20"
                        >
                          <button
                            type="button"
                            onClick={() => setAiFaqOpenIndex(isOpen ? null : idx)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-3 text-left"
                          >
                            <span className="text-sm font-semibold text-white">{faq.question}</span>
                            <ArrowRight
                              className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-90 text-yellow-400" : ""}`}
                            />
                          </button>
                          {isOpen && (
                            <div className="px-3 pb-3">
                              <p className="text-slate-400 leading-relaxed text-sm">{faq.answer}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="border border-emerald-200 rounded-2xl p-6 bg-gradient-to-r from-emerald-50 via-white to-slate-50 shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Top Up to Continue</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">Unlock more underwriting runs instantly.</p>
                    <p className="text-sm text-slate-600 mt-1">Get immediate access to credits and keep files moving.</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-5">
                  <button className="text-left border border-emerald-200 rounded-xl px-4 py-4 bg-white hover:border-emerald-300 hover:shadow transition">
                    <p className="text-sm font-semibold text-slate-900">Buy 2,000 Credits - $20</p>
                    <p className="text-xs text-emerald-700 mt-1">Instant Access.</p>
                  </button>
                  <button className="text-left border border-emerald-300 rounded-xl px-4 py-4 bg-emerald-50 hover:border-emerald-400 hover:shadow transition">
                    <p className="text-sm font-semibold text-slate-900">Buy 6,000 Credits - $50</p>
                    <p className="text-xs text-emerald-700 mt-1">(Best Seller)</p>
                  </button>
                  <button className="text-left border border-slate-200 rounded-xl px-4 py-4 bg-white hover:border-emerald-300 hover:shadow transition">
                    <p className="text-sm font-semibold text-slate-900">Buy 30,000 Credits - $200</p>
                    <p className="text-xs text-emerald-700 mt-1">Save 33%.</p>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      <PreviewModal
        previewDoc={previewDoc}
        closePreview={closePreview}
        onSave={handleSavePreview}
      />

      <CaseDetailsModal
        selectedCase={selectedCase}
        onClose={() => setSelectedCase(null)}
        onStatusChange={handleStatusChange}
        onDocumentUpload={handleDocumentUpload}
        uploadMessage={uploadMessage}
        caseFiles={caseFiles}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        isStatusUpdating={isStatusUpdating}
        handlePreview={handlePreview}
        aiReports={selectedCase ? (aiReports[selectedCase.id || selectedCase.caseId || selectedCase.case_id] || []) : []}
        aiMessage={aiMessage}
        onGenerateAi={handleAiGenerateClick}
        caseError={caseError}
        isAiGenerating={isAiGenerating}
        aiPipelineStatus={pipelineStatus}
        pipelineRuns={pipelineRuns}
        isAccountSettingsOpen={isAccountSettingsOpen}
        caseComments={selectedCase ? (caseComments[selectedCase.id || selectedCase.caseId || selectedCase.case_id] || []) : []}
        onAddComment={handleAddComment}
        newComment={newComment}
        setNewComment={setNewComment}
        isCommenting={isCommenting}
        showSampleReport={showSampleReport}
        setShowSampleReport={setShowSampleReport}
        autoDownloadSample={autoDownloadSample}
        setAutoDownloadSample={setAutoDownloadSample}
        isCaseLoading={isCaseLoading}
        onDeleteFile={handleDeleteFile}
        isDeletingFile={isDeletingFile}
        fileDeleteTarget={fileDeleteTarget}
        isCaseHydrating={isCaseHydrating}
        onUpgradePlan={handleOpenPortal}
        featureFlags={featureFlags}
        userTier={userMeta?.membership || 'free'}
        userCredits={userMeta?.credit || 0}
        onRefreshCredits={refreshSubscriptionMeta}
        onCaseUpdate={handleCaseUpdate}
      />
      {
        deleteCandidate && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur-sm">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Delete Document</p>
                  <p className="text-lg font-bold dark:text-white text-slate-900 break-words">{deleteCandidate.name}</p>
                  <p className="text-sm dark:text-slate-400 text-slate-500 mt-1">Are you sure you want to delete this file?</p>
                </div>
                <button onClick={cancelDelete} className="dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 rounded-lg border dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  disabled={isDeletingFile}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteFile}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors flex items-center gap-2 disabled:opacity-60"
                  disabled={isDeletingFile}
                >
                  {isDeletingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        isDuplicatePromptOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur-sm">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Duplicate files</p>
                  <p className="text-lg font-bold dark:text-white text-slate-900">Replace existing files?</p>
                  <p className="text-sm dark:text-slate-400 text-slate-500 mt-1">
                    These files already exist for this case:
                  </p>
                </div>
                <button
                  onClick={cancelDuplicateUpload}
                  className="p-2 rounded-full dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ul className="text-sm dark:text-slate-200 text-slate-700 dark:bg-slate-950/60 bg-slate-100 border dark:border-slate-800 border-slate-200 rounded-lg p-3 space-y-1 max-h-32 overflow-y-auto">
                {duplicateFiles.map((name) => (
                  <li key={name} className="truncate">{name}</li>
                ))}
              </ul>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDuplicateUpload}
                  className="px-4 py-2 rounded-lg border dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDuplicateUpload}
                  className="px-4 py-2 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors"
                >
                  Override files
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        isPaymentOpen && (
          <div className="fixed inset-0 z-[145] flex items-center justify-center p-4 dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur-sm">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                    {(() => {
                      const storedType = typeof window !== "undefined"
                        ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                        : checkoutType;
                      return storedType === "topup" ? "Top Up" : "Upgrade";
                    })()}
                  </p>
                  <p className="text-lg font-bold dark:text-white text-slate-900">
                    {(() => {
                      const storedType = typeof window !== "undefined"
                        ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                        : checkoutType;
                      if (storedType === "topup") return "Credit Pack";

                      const storedTier = typeof window !== "undefined"
                        ? window.localStorage.getItem("cm_checkout_tier") || "starter"
                        : "starter";
                      return TIER_DISPLAY_INFO[storedTier]?.name || "Starter Membership";
                    })()}
                  </p>
                  <p className="text-sm text-slate-400">
                    {(() => {
                      const storedType = typeof window !== "undefined"
                        ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                        : checkoutType;
                      if (storedType === "topup") return "Purchase credits to keep reports moving.";

                      const storedTier = typeof window !== "undefined"
                        ? window.localStorage.getItem("cm_checkout_tier") || "starter"
                        : "starter";
                      return TIER_DISPLAY_INFO[storedTier]?.description || "50 cases • Fast processing • Priority support";
                    })()}
                  </p>
                </div>
                <button
                  onClick={() => setIsPaymentOpen(false)}
                  className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {paymentError && (
                <div className="text-sm text-yellow-300 bg-yellow-500/10 border border-yellow-500/40 rounded-lg px-3 py-2">
                  {paymentError}
                </div>
              )}

              {checkoutMessage && (
                <div className="text-sm text-slate-200 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2">
                  {checkoutMessage}
                </div>
              )}

              {isPaymentProcessing && (
                <div className="flex items-center gap-2 text-sm text-yellow-200 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{paymentStep || "Processing payment..."}</span>
                </div>
              )}

              {checkoutSuccess ? (
                <div className="space-y-4 text-sm text-slate-200">
                  <div className="flex items-start gap-3 bg-emerald-600/10 border border-emerald-500/40 rounded-lg p-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-300 font-bold">
                      ✓
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">Success</p>
                      <h4 className="text-lg font-semibold text-white">
                        {(() => {
                          // Read from localStorage as source of truth since state might not be updated yet
                          const storedType = typeof window !== "undefined"
                            ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                            : checkoutType;
                          if (storedType === "topup") return "Credits added successfully!";

                          const storedTier = typeof window !== "undefined"
                            ? window.localStorage.getItem("cm_checkout_tier") || "starter"
                            : "starter";
                          const tierName = storedTier.charAt(0).toUpperCase() + storedTier.slice(1);
                          return `Subscription to ${tierName} Plan successful!`;
                        })()}
                      </h4>
                      <p className="text-sm text-slate-300">
                        {(() => {
                          const storedType = typeof window !== "undefined"
                            ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                            : checkoutType;
                          return storedType === "topup"
                            ? "You can manage receipts in the Stripe customer portal."
                            : "Manage your billing details or receipts in the Stripe customer portal.";
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={handleCheckoutSuccessAcknowledge}
                      disabled={isPaymentProcessing}
                      className="w-full py-2 rounded-lg bg-slate-100 text-slate-900 font-semibold hover:bg-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {isPaymentProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                      OK
                    </button>
                    <button
                      type="button"
                      onClick={handleOpenPortal}
                      disabled={isPaymentProcessing}
                      className="w-full py-2 rounded-lg bg-slate-800 text-slate-100 font-semibold hover:bg-slate-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {isPaymentProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                      Manage billing in Stripe
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Credits and membership update after Stripe webhooks finish processing.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-slate-950/60 border border-slate-800 rounded-xl p-4">
                    <div className="flex items-center gap-4">
                      <div className="bg-slate-800 rounded-lg p-3">
                        <Logo className="h-12 w-auto" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Starter Plan</p>
                        <h3 className="text-2xl font-bold text-white">$20.00 / month</h3>
                        <p className="text-xs text-slate-400">500 monthly credits • 12-hour processing • Priority support</p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-slate-400">
                      <p className="font-semibold text-slate-300">Stripe Checkout</p>
                      <p>PCI compliant • Redirect flow</p>
                    </div>
                  </div>

                  <button
                    id="checkout-and-portal-button"
                    type="button"
                    onClick={handleStartCheckout}
                    disabled={isPaymentProcessing}
                    className="w-full py-2 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {isPaymentProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                    Start checkout
                  </button>

                  <button
                    type="button"
                    onClick={handleOpenPortal}
                    disabled={isPaymentProcessing}
                    className="w-full py-2 rounded-lg bg-slate-800 text-slate-100 font-semibold hover:bg-slate-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {isPaymentProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                    Manage billing (Stripe portal)
                  </button>

                  <p className="text-[11px] text-slate-500">
                    We’ll redirect you to Stripe Checkout. On success, you’ll land back here with a session id to open the
                    billing portal.
                  </p>
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Celebration Overlay - Confetti, Flying Emoji, Big Text */}
      <CelebrationOverlay state={celebrationState} />

      {/* Celebration Modal - Big and Bold */}
      <AnimatePresence>
        {celebrationStatus && celebrationStatus !== "success" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[170] flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.5, y: 100, rotateX: 45 }}
              animate={{ scale: 1, y: 0, rotateX: 0 }}
              exit={{ scale: 0.8, y: -50, opacity: 0 }}
              transition={{ type: "spring", bounce: 0.4, duration: 0.8 }}
              className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-emerald-500/50 rounded-3xl shadow-[0_0_100px_rgba(16,185,129,0.3)] w-full max-w-2xl overflow-hidden"
            >
              {/* Animated background glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-yellow-500/10 to-purple-500/20 animate-pulse" />
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-500/30 rounded-full blur-3xl animate-bounce" style={{ animationDuration: '3s' }} />
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-yellow-500/30 rounded-full blur-3xl animate-bounce" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />

              <div className="relative p-12 space-y-6 text-center">
                {celebrationStatus === "Approved" ? (
                  <>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3, type: "spring", bounce: 0.5 }}
                      className="text-8xl mb-4"
                    >
                      🎊
                    </motion.div>
                    <motion.h3
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-green-300 to-teal-400 drop-shadow-lg"
                    >
                      APPROVED!
                    </motion.h3>
                    <motion.p
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="text-slate-300 text-xl"
                    >
                      All required actions are complete. The loan is officially approved.
                    </motion.p>
                  </>
                ) : (
                  <>
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: 0.3, type: "spring", bounce: 0.5 }}
                      className="text-8xl mb-4"
                    >
                      🏆
                    </motion.div>
                    <motion.h3
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 drop-shadow-lg"
                    >
                      CLEAR TO CLOSE!
                    </motion.h3>
                    <motion.p
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="text-slate-300 text-xl"
                    >
                      Underwriting is complete. This loan is ready for closing!
                    </motion.p>
                  </>
                )}
                <motion.button
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-6 px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-lg font-bold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all"
                  onClick={() => {
                    setCelebrationStatus(null);
                    triggerCelebration(); // Trigger confetti, flying emoji, and celebration message
                  }}
                >
                  Continue
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SampleSummaryModal
        isOpen={showSampleReport}
        onClose={() => {
          setShowSampleReport(false);
          setAutoDownloadSample(false);
        }}
        autoDownload={autoDownloadSample}
      />
      {/* Team Chat Widget */}
      <TaskCopilotPrototype userEmail={userEmail} initialConversations={chatConversations} onUnreadCountChange={setUnreadChatCount} />

      {/* AMA Assistant - Global AI Chat (Ultra+ only) */}
      {featureFlags.hasAMA && (
        <AMAChat userId={userEmail} onCaseClick={handleAMACaseClick} onTaskClick={handleAMATaskClick} />
      )}
    </div >
  );
};

export default Dashboard;
